import type { NamespaceCatalog } from '@/i18n/ns';

const workspaceEditorsCopy = {
  en: {
    suggestionNewPageUnder: 'New page “{title}” under {parent}',
    suggestionMoveUnder: 'Move this page under {parent}',
    suggestionArchive: 'Archive this page',
    suggestionRenameTo: 'Rename this page to “{title}”',
    suggestionOverlapNote:
      'It changes the same text as another suggestion, so its change is shown here instead of in the page.',
    suggestionMismatchNote:
      'Its change could not be shown against the page as it reads now. Reload the page to review it.',
    showChange: 'show it in the page',
    pageMove: 'Move to…',
    pageArchive: 'Archive',
    pageRestore: 'Restore page',
    pageDeletePermanently: 'Delete permanently',
    pageExportMarkdown: 'Export Markdown',
    pageActions: 'Page actions',
    pageMoveArchiveReason: 'Move or archive the pages under it first.',
    expectedResult: 'Expected result',
    expectedResultHint: 'What a successful run should deliver.',
    resources: 'Knowledge & connections',
    resourcesHint: 'Where to look. Each run uses only resources its acting person can access.',
    boundaries: 'Boundaries & actions',
    boundariesHint: 'What to do or avoid. Every connected action still needs human approval.',
    skillsOptional: 'Skills · optional',
    skillsOptionalNote: 'Reusable methods this agent can use. It works without any Skills.',
    notSpecified: 'Not specified',
    newResultEachRun: 'A new Result each run',
    keepResultUpdated: 'Keep one Result up to date',
    whatEachRunDelivers: 'What each run delivers',
    editAccessRequested: 'Edit access requested · waiting for an answer.',
    askForEditAccess: 'To change it, ask for edit access or make your own copy.',
    newAgentGoalHint: 'What it should achieve.',
    newAgentScheduleHint: 'On demand, or on a schedule you choose.',
    newAgentInstructionsHint: 'How it should do the work.',
    newAgentSkillsHint: 'Finds the Skills that fit each run, the same way a chat does.',
    newAgentUsesHint: 'Company knowledge and the connections it may use.',
    newAgentDeliversHint: 'A Result: new each run, or one kept up to date.',
    agentTab: 'Agent',
    runsTab: 'Runs',
    versionsTab: 'Versions',
    testRunTab: 'Test run',
    versionMeta: 'Version {version}',
    changeProjectBriefPrompt: 'Change the project brief:',
    changeProjectPrompt: 'Change this project:',
    changeAgentUsesPrompt:
      'Help me change what agent {id} ({name}) uses: its knowledge, connections and resources. Read its current definition first.',
    makeChatProjectNamed: 'Make this chat a project called {name}.',
    makeChatProject: 'Make this chat a project.',
    projectBriefInstruction: 'Use this for the brief:\n{brief}',
    couldntCreateProject: "Couldn't create the project.",
    treeToggle: '{action} the pages under {title}',
    treeHide: 'Hide',
    treeShow: 'Show',
    goalHint: 'What should this agent achieve?',
    instructionsHint: 'How the agent should approach the work and make decisions.',
    schedulePresetOff: 'Off',
    schedulePresetHourly: 'Every hour',
    schedulePresetDaily: 'Every day',
    schedulePresetWeekdays: 'Weekdays',
    schedulePresetWeekly: 'Weekly',
    schedulePresetCustom: 'Custom',
    pageFallbackAnother: 'another page',
    pageFallbackOverview: 'the company overview',
    noteOptional: 'Note (optional)',
    whyThisChange: 'Why this change?',
    discardChangesConfirm: 'Discard your changes? Nothing has been saved.',
    editAgentChatPrompt: 'Help me improve agent {id} ({name}). Read its current definition first.',
    revisionLabel: 'Revision {number}',
    movePageTitle: 'Move “{title}”',
    deletePageTitle: 'Delete “{title}” permanently?',
    archivePageTitle: 'Archive “{title}”?',
    suggestArchivePageTitle: 'Suggest archiving “{title}”',
    editedLabel: 'Edited',
    editedPageSummary: 'Edited the page: “{note}”',
    revisionConflict:
      'The page changed while you were reviewing it. It is reloaded: review the suggestion again.',
  },
  bg: {
    suggestionNewPageUnder: 'Нова страница „{title}“ под {parent}',
    suggestionMoveUnder: 'Премести тази страница под {parent}',
    suggestionArchive: 'Архивирай тази страница',
    suggestionRenameTo: 'Преименувай тази страница на „{title}“',
    suggestionOverlapNote:
      'Тя променя същия текст като друго предложение, затова промяната му е показана тук, вместо на страницата.',
    suggestionMismatchNote:
      'Промяната му не може да бъде показана спрямо текущото съдържание на страницата. Презаредете страницата, за да я прегледате.',
    showChange: 'покажи я на страницата',
    pageMove: 'Премести в…',
    pageArchive: 'Архивирай',
    pageRestore: 'Възстанови страницата',
    pageDeletePermanently: 'Изтрий завинаги',
    pageExportMarkdown: 'Експортирай като Markdown',
    pageActions: 'Действия за страницата',
    pageMoveArchiveReason: 'Първо преместете или архивирайте страниците под нея.',
    expectedResult: 'Очакван резултат',
    expectedResultHint: 'Какво трябва да предостави успешно изпълнение.',
    resources: 'Знания и връзки',
    resourcesHint:
      'Къде да търси. Всяко изпълнение използва само ресурсите, до които има достъп лицето, което го изпълнява.',
    boundaries: 'Ограничения и действия',
    boundariesHint:
      'Какво да прави или избягва. Всяко свързано действие все пак изисква одобрение от човек.',
    skillsOptional: 'Умения · по избор',
    skillsOptionalNote:
      'Методи за многократна употреба, които този агент може да използва. Работи и без никакви умения.',
    notSpecified: 'Не е посочено',
    newResultEachRun: 'Нов резултат при всяко изпълнение',
    keepResultUpdated: 'Поддържай един резултат актуален',
    whatEachRunDelivers: 'Какво предоставя всяко изпълнение',
    editAccessRequested: 'Заявено е право за редактиране · в очакване на отговор.',
    askForEditAccess:
      'За да го промените, поискайте право за редактиране или направете собствено копие.',
    newAgentGoalHint: 'Какво трябва да постигне.',
    newAgentScheduleHint: 'При поискване или по график, който изберете.',
    newAgentInstructionsHint: 'Как трябва да свърши работата.',
    newAgentSkillsHint: 'Намира подходящите за всяко изпълнение умения по същия начин като чата.',
    newAgentUsesHint: 'Фирмени знания и връзките, които може да използва.',
    newAgentDeliversHint:
      'Резултат: нов при всяко изпълнение или един, който се поддържа актуален.',
    agentTab: 'Агент',
    runsTab: 'Изпълнения',
    versionsTab: 'Версии',
    testRunTab: 'Тестово изпълнение',
    versionMeta: 'Версия {version}',
    changeProjectBriefPrompt: 'Промени описанието на проекта:',
    changeProjectPrompt: 'Промени този проект:',
    changeAgentUsesPrompt:
      'Помогни ми да променя какво използва агентът {id} ({name}): неговите знания, връзки и ресурси. Първо прочети текущата му дефиниция.',
    makeChatProjectNamed: 'Превърни този чат в проект с име {name}.',
    makeChatProject: 'Превърни този чат в проект.',
    projectBriefInstruction: 'Използвай това за описанието:\n{brief}',
    couldntCreateProject: 'Проектът не можа да бъде създаден.',
    treeToggle: '{action} страниците под {title}',
    treeHide: 'Скрий',
    treeShow: 'Покажи',
    goalHint: 'Какво трябва да постигне този агент?',
    instructionsHint: 'Как агентът трябва да подхожда към работата и да взема решения.',
    schedulePresetOff: 'Изкл.',
    schedulePresetHourly: 'На всеки час',
    schedulePresetDaily: 'Всеки ден',
    schedulePresetWeekdays: 'Работни дни',
    schedulePresetWeekly: 'Всяка седмица',
    schedulePresetCustom: 'По избор',
    pageFallbackAnother: 'друга страница',
    pageFallbackOverview: 'общият преглед на компанията',
    noteOptional: 'Бележка (по избор)',
    whyThisChange: 'Защо е нужна тази промяна?',
    discardChangesConfirm: 'Да отхвърлите ли промените? Нищо не е запазено.',
    editAgentChatPrompt:
      'Помогнете ми да подобря агента {id} ({name}). Първо прочетете текущата му дефиниция.',
    revisionLabel: 'Версия {number}',
    movePageTitle: 'Преместване на „{title}“',
    deletePageTitle: 'Да се изтрие ли „{title}“ завинаги?',
    archivePageTitle: 'Да се архивира ли „{title}“?',
    suggestArchivePageTitle: 'Предложи архивиране на „{title}“',
    editedLabel: 'Редактирано',
    editedPageSummary: 'Страницата е редактирана: „{note}“',
    revisionConflict:
      'Страницата се промени, докато я преглеждахте. Тя е презаредена: прегледайте предложението отново.',
  },
  hr: {
    suggestionNewPageUnder: 'Nova stranica „{title}” pod {parent}',
    suggestionMoveUnder: 'Premjesti ovu stranicu pod {parent}',
    suggestionArchive: 'Arhiviraj ovu stranicu',
    suggestionRenameTo: 'Preimenuj ovu stranicu u „{title}”',
    suggestionOverlapNote:
      'Mijenja isti tekst kao drugi prijedlog pa je njegova promjena prikazana ovdje umjesto na stranici.',
    suggestionMismatchNote:
      'Njegovu promjenu nije moguće prikazati u odnosu na trenutačni sadržaj stranice. Ponovno učitajte stranicu da biste je pregledali.',
    showChange: 'prikaži je na stranici',
    pageMove: 'Premjesti…',
    pageArchive: 'Arhiviraj',
    pageRestore: 'Vrati stranicu',
    pageDeletePermanently: 'Trajno izbriši',
    pageExportMarkdown: 'Izvezi Markdown',
    pageActions: 'Radnje na stranici',
    pageMoveArchiveReason:
      'Najprije premjestite ili arhivirajte stranice koje se nalaze ispod nje.',
    expectedResult: 'Očekivani rezultat',
    expectedResultHint: 'Što bi uspješno pokretanje trebalo isporučiti.',
    resources: 'Znanje i veze',
    resourcesHint:
      'Gdje tražiti. Svako pokretanje koristi samo resurse kojima osoba koja ga pokreće ima pristup.',
    boundaries: 'Ograničenja i radnje',
    boundariesHint:
      'Što učiniti ili izbjegavati. Za svaku povezanu radnju i dalje je potrebno odobrenje osobe.',
    skillsOptional: 'Vještine · neobavezno',
    skillsOptionalNote:
      'Ponovno upotrebljive metode koje ovaj agent može koristiti. Radi i bez ikakvih vještina.',
    notSpecified: 'Nije navedeno',
    newResultEachRun: 'Novi rezultat pri svakom pokretanju',
    keepResultUpdated: 'Održavaj jedan rezultat ažurnim',
    whatEachRunDelivers: 'Što svako pokretanje isporučuje',
    editAccessRequested: 'Zatražen je pristup za uređivanje · čeka se odgovor.',
    askForEditAccess:
      'Da biste ga promijenili, zatražite pristup za uređivanje ili izradite vlastitu kopiju.',
    newAgentGoalHint: 'Što bi trebao postići.',
    newAgentScheduleHint: 'Na zahtjev ili prema rasporedu koji odaberete.',
    newAgentInstructionsHint: 'Kako bi trebao obaviti posao.',
    newAgentSkillsHint:
      'Za svako pokretanje pronalazi odgovarajuće vještine, na isti način kao i chat.',
    newAgentUsesHint: 'Znanje tvrtke i veze koje smije koristiti.',
    newAgentDeliversHint: 'Rezultat: novi pri svakom pokretanju ili jedan koji se održava ažurnim.',
    agentTab: 'Agent',
    runsTab: 'Pokretanja',
    versionsTab: 'Verzije',
    testRunTab: 'Probno pokretanje',
    versionMeta: 'Verzija {version}',
    changeProjectBriefPrompt: 'Promijeni sažetak projekta:',
    changeProjectPrompt: 'Promijeni ovaj projekt:',
    changeAgentUsesPrompt:
      'Pomozi mi promijeniti što agent {id} ({name}) koristi: svoje znanje, veze i resurse. Najprije pročitaj njegovu trenutačnu definiciju.',
    makeChatProjectNamed: 'Pretvori ovaj chat u projekt pod nazivom {name}.',
    makeChatProject: 'Pretvori ovaj chat u projekt.',
    projectBriefInstruction: 'Upotrijebi ovo za sažetak:\n{brief}',
    couldntCreateProject: 'Nije moguće izraditi projekt.',
    treeToggle: '{action} stranice ispod naslova {title}',
    treeHide: 'Sakrij',
    treeShow: 'Prikaži',
    goalHint: 'Što bi ovaj agent trebao postići?',
    instructionsHint: 'Kako bi agent trebao pristupiti radu i donositi odluke.',
    schedulePresetOff: 'Isključeno',
    schedulePresetHourly: 'Svaki sat',
    schedulePresetDaily: 'Svaki dan',
    schedulePresetWeekdays: 'Radni dani',
    schedulePresetWeekly: 'Tjedno',
    schedulePresetCustom: 'Prilagođeno',
    pageFallbackAnother: 'drugu stranicu',
    pageFallbackOverview: 'pregled tvrtke',
    noteOptional: 'Napomena (nije obavezno)',
    whyThisChange: 'Zašto ova promjena?',
    discardChangesConfirm: 'Odbaciti promjene? Ništa nije spremljeno.',
    editAgentChatPrompt:
      'Pomozite mi poboljšati agenta {id} ({name}). Najprije pročitajte njegovu trenutačnu definiciju.',
    revisionLabel: 'Revizija {number}',
    movePageTitle: 'Premjestiti „{title}“',
    deletePageTitle: 'Trajno izbrisati „{title}“?',
    archivePageTitle: 'Arhivirati „{title}“?',
    suggestArchivePageTitle: 'Predložiti arhiviranje „{title}“',
    editedLabel: 'Uređeno',
    editedPageSummary: 'Uređena stranica: „{note}“',
    revisionConflict:
      'Stranica se promijenila dok ste je pregledavali. Ponovno je učitana: ponovno pregledajte prijedlog.',
  },
  cs: {
    suggestionNewPageUnder: 'Nová stránka „{title}“ pod stránkou {parent}',
    suggestionMoveUnder: 'Přesunout tuto stránku pod {parent}',
    suggestionArchive: 'Archivovat tuto stránku',
    suggestionRenameTo: 'Přejmenovat tuto stránku na „{title}“',
    suggestionOverlapNote:
      'Mění stejný text jako jiný návrh, proto se jeho změna zobrazuje zde místo na stránce.',
    suggestionMismatchNote:
      'Jeho změnu nelze zobrazit vůči aktuálnímu znění stránky. Znovu načtěte stránku a zkontrolujte ji.',
    showChange: 'zobrazit ji na stránce',
    pageMove: 'Přesunout…',
    pageArchive: 'Archivovat',
    pageRestore: 'Obnovit stránku',
    pageDeletePermanently: 'Trvale smazat',
    pageExportMarkdown: 'Exportovat Markdown',
    pageActions: 'Akce stránky',
    pageMoveArchiveReason: 'Nejprve přesuňte nebo archivujte stránky, které se pod ní nacházejí.',
    expectedResult: 'Očekávaný výsledek',
    expectedResultHint: 'Co má úspěšné spuštění přinést.',
    resources: 'Znalosti a připojení',
    resourcesHint:
      'Kde hledat. Každé spuštění používá pouze zdroje, ke kterým má jednající osoba přístup.',
    boundaries: 'Hranice a akce',
    boundariesHint:
      'Co dělat nebo čemu se vyhnout. Každá připojená akce stále vyžaduje schválení člověkem.',
    skillsOptional: 'Dovednosti · volitelné',
    skillsOptionalNote:
      'Opakovaně použitelné metody, které může tento agent využívat. Funguje i bez jakýchkoli dovedností.',
    notSpecified: 'Neuvedeno',
    newResultEachRun: 'Nový výsledek při každém spuštění',
    keepResultUpdated: 'Průběžně aktualizovat jeden výsledek',
    whatEachRunDelivers: 'Co přináší každé spuštění',
    editAccessRequested: 'Požadován přístup k úpravám · čeká se na odpověď.',
    askForEditAccess:
      'Chcete-li jej změnit, požádejte o přístup k úpravám nebo si vytvořte vlastní kopii.',
    newAgentGoalHint: 'Čeho má dosáhnout.',
    newAgentScheduleHint: 'Na vyžádání nebo podle vámi zvoleného rozvrhu.',
    newAgentInstructionsHint: 'Jak má práci provést.',
    newAgentSkillsHint: 'Pro každé spuštění vyhledá vhodné dovednosti stejně jako chat.',
    newAgentUsesHint: 'Znalosti společnosti a připojení, která může používat.',
    newAgentDeliversHint: 'Výsledek: nový při každém spuštění, nebo jeden průběžně aktualizovaný.',
    agentTab: 'Agent',
    runsTab: 'Spuštění',
    versionsTab: 'Verze',
    testRunTab: 'Testovací spuštění',
    versionMeta: 'Verze {version}',
    changeProjectBriefPrompt: 'Změňte zadání projektu:',
    changeProjectPrompt: 'Změňte tento projekt:',
    changeAgentUsesPrompt:
      'Pomozte mi změnit, co agent {id} ({name}) používá: jeho znalosti, připojení a zdroje. Nejprve si přečtěte jeho aktuální definici.',
    makeChatProjectNamed: 'Proměňte tento chat v projekt s názvem {name}.',
    makeChatProject: 'Proměňte tento chat v projekt.',
    projectBriefInstruction: 'Pro zadání použijte toto:\n{brief}',
    couldntCreateProject: 'Projekt se nepodařilo vytvořit.',
    treeToggle: '{action} stránky pod {title}',
    treeHide: 'Skrýt',
    treeShow: 'Zobrazit',
    goalHint: 'Čeho má tento agent dosáhnout?',
    instructionsHint: 'Jak má agent k práci přistupovat a rozhodovat se.',
    schedulePresetOff: 'Vypnuto',
    schedulePresetHourly: 'Každou hodinu',
    schedulePresetDaily: 'Každý den',
    schedulePresetWeekdays: 'Pracovní dny',
    schedulePresetWeekly: 'Týdně',
    schedulePresetCustom: 'Vlastní',
    pageFallbackAnother: 'jiná stránka',
    pageFallbackOverview: 'přehled společnosti',
    noteOptional: 'Poznámka (volitelné)',
    whyThisChange: 'Proč tuto změnu?',
    discardChangesConfirm: 'Chcete zahodit změny? Nic nebylo uloženo.',
    editAgentChatPrompt:
      'Pomoz mi vylepšit agenta {id} ({name}). Nejprve si přečti jeho aktuální definici.',
    revisionLabel: 'Revize {number}',
    movePageTitle: 'Přesunout „{title}“',
    deletePageTitle: 'Trvale smazat „{title}“?',
    archivePageTitle: 'Archivovat „{title}“?',
    suggestArchivePageTitle: 'Navrhnout archivaci „{title}“',
    editedLabel: 'Upraveno',
    editedPageSummary: 'Upravili jste stránku: „{note}“',
    revisionConflict:
      'Stránka se během kontroly změnila. Byla znovu načtena: zkontrolujte návrh znovu.',
  },
  da: {
    suggestionNewPageUnder: 'Ny side “{title}” under {parent}',
    suggestionMoveUnder: 'Flyt denne side under {parent}',
    suggestionArchive: 'Arkivér denne side',
    suggestionRenameTo: 'Omdøb denne side til “{title}”',
    suggestionOverlapNote:
      'Den ændrer den samme tekst som et andet forslag, så ændringen vises her i stedet for på siden.',
    suggestionMismatchNote:
      'Ændringen kunne ikke vises i forhold til siden, som den ser ud nu. Genindlæs siden for at gennemgå den.',
    showChange: 'vis den på siden',
    pageMove: 'Flyt til…',
    pageArchive: 'Arkivér',
    pageRestore: 'Gendan side',
    pageDeletePermanently: 'Slet permanent',
    pageExportMarkdown: 'Eksportér Markdown',
    pageActions: 'Sidehandlinger',
    pageMoveArchiveReason: 'Flyt eller arkivér først siderne under den.',
    expectedResult: 'Forventet resultat',
    expectedResultHint: 'Det, en vellykket kørsel skal levere.',
    resources: 'Viden og forbindelser',
    resourcesHint:
      'Hvor der skal kigges. Hver kørsel bruger kun ressourcer, som den handlende person har adgang til.',
    boundaries: 'Grænser og handlinger',
    boundariesHint:
      'Hvad der skal gøres eller undgås. Alle forbundne handlinger kræver stadig menneskelig godkendelse.',
    skillsOptional: 'Færdigheder · valgfrit',
    skillsOptionalNote:
      'Genanvendelige metoder, som denne agent kan bruge. Den fungerer uden færdigheder.',
    notSpecified: 'Ikke angivet',
    newResultEachRun: 'Et nyt resultat for hver kørsel',
    keepResultUpdated: 'Hold ét resultat opdateret',
    whatEachRunDelivers: 'Det, hver kørsel leverer',
    editAccessRequested: 'Redigeringsadgang anmodet · venter på svar.',
    askForEditAccess:
      'Hvis du vil ændre det, skal du anmode om redigeringsadgang eller lave din egen kopi.',
    newAgentGoalHint: 'Det, den skal opnå.',
    newAgentScheduleHint: 'Efter behov eller efter en tidsplan, du vælger.',
    newAgentInstructionsHint: 'Hvordan den skal udføre arbejdet.',
    newAgentSkillsHint:
      'Finder de færdigheder, der passer til hver kørsel, på samme måde som en chat gør.',
    newAgentUsesHint: 'Virksomhedens viden og de forbindelser, den må bruge.',
    newAgentDeliversHint: 'Et resultat: et nyt for hver kørsel eller ét, der holdes opdateret.',
    agentTab: 'Agent',
    runsTab: 'Kørsler',
    versionsTab: 'Versioner',
    testRunTab: 'Testkørsel',
    versionMeta: 'Version {version}',
    changeProjectBriefPrompt: 'Rediger projektbeskrivelsen:',
    changeProjectPrompt: 'Rediger dette projekt:',
    changeAgentUsesPrompt:
      'Hjælp mig med at ændre, hvad agenten {id} ({name}) bruger: dens viden, forbindelser og ressourcer. Læs først dens aktuelle definition.',
    makeChatProjectNamed: 'Gør denne chat til et projekt kaldet {name}.',
    makeChatProject: 'Gør denne chat til et projekt.',
    projectBriefInstruction: 'Brug dette som beskrivelse:\n{brief}',
    couldntCreateProject: 'Projektet kunne ikke oprettes.',
    treeToggle: '{action} siderne under {title}',
    treeHide: 'Skjul',
    treeShow: 'Vis',
    goalHint: 'Hvad skal denne agent opnå?',
    instructionsHint: 'Hvordan agenten skal gribe arbejdet an og træffe beslutninger.',
    schedulePresetOff: 'Fra',
    schedulePresetHourly: 'Hver time',
    schedulePresetDaily: 'Hver dag',
    schedulePresetWeekdays: 'Hverdage',
    schedulePresetWeekly: 'Ugentligt',
    schedulePresetCustom: 'Tilpasset',
    pageFallbackAnother: 'en anden side',
    pageFallbackOverview: 'virksomhedsoversigten',
    noteOptional: 'Bemærkning (valgfri)',
    whyThisChange: 'Hvorfor denne ændring?',
    discardChangesConfirm: 'Kassér dine ændringer? Intet er blevet gemt.',
    editAgentChatPrompt:
      'Hjælp mig med at forbedre agenten {id} ({name}). Læs først dens aktuelle definition.',
    revisionLabel: 'Revision {number}',
    movePageTitle: 'Flyt “{title}”',
    deletePageTitle: 'Slet “{title}” permanent?',
    archivePageTitle: 'Arkivér “{title}”?',
    suggestArchivePageTitle: 'Foreslå arkivering af “{title}”',
    editedLabel: 'Redigeret',
    editedPageSummary: 'Siden blev redigeret: “{note}”',
    revisionConflict:
      'Siden blev ændret, mens du gennemgik den. Den er indlæst igen: Gennemgå forslaget igen.',
  },
  nl: {
    suggestionNewPageUnder: 'Nieuwe pagina “{title}” onder {parent}',
    suggestionMoveUnder: 'Deze pagina onder {parent} plaatsen',
    suggestionArchive: 'Deze pagina archiveren',
    suggestionRenameTo: 'Deze pagina hernoemen naar “{title}”',
    suggestionOverlapNote:
      'Deze wijziging betreft dezelfde tekst als een andere suggestie. Daarom wordt de wijziging hier weergegeven in plaats van op de pagina.',
    suggestionMismatchNote:
      'De wijziging kon niet worden weergegeven tegen de huidige versie van de pagina. Laad de pagina opnieuw om de wijziging te bekijken.',
    showChange: 'in de pagina weergeven',
    pageMove: 'Verplaatsen naar…',
    pageArchive: 'Archiveren',
    pageRestore: 'Pagina herstellen',
    pageDeletePermanently: 'Permanent verwijderen',
    pageExportMarkdown: 'Markdown exporteren',
    pageActions: 'Pagina-acties',
    pageMoveArchiveReason: "Verplaats of archiveer eerst de pagina's die eronder staan.",
    expectedResult: 'Verwacht resultaat',
    expectedResultHint: 'Wat een geslaagde uitvoering moet opleveren.',
    resources: 'Kennis en verbindingen',
    resourcesHint:
      'Waar je moet zoeken. Elke uitvoering gebruikt alleen bronnen waartoe de persoon namens wie de agent handelt toegang heeft.',
    boundaries: 'Grenzen en acties',
    boundariesHint:
      'Wat je wel of niet moet doen. Voor elke verbonden actie is nog steeds goedkeuring van een persoon nodig.',
    skillsOptional: 'Vaardigheden · optioneel',
    skillsOptionalNote:
      'Herbruikbare methoden die deze agent kan gebruiken. De agent werkt ook zonder Vaardigheden.',
    notSpecified: 'Niet opgegeven',
    newResultEachRun: 'Bij elke uitvoering een nieuw resultaat',
    keepResultUpdated: 'Eén resultaat actueel houden',
    whatEachRunDelivers: 'Wat elke uitvoering oplevert',
    editAccessRequested: 'Toegang om te bewerken aangevraagd · wachten op antwoord.',
    askForEditAccess: 'Vraag toegang om te bewerken of maak een eigen kopie om dit te wijzigen.',
    newAgentGoalHint: 'Wat het moet bereiken.',
    newAgentScheduleHint: 'Op verzoek of volgens een schema dat je kiest.',
    newAgentInstructionsHint: 'Hoe het werk moet worden uitgevoerd.',
    newAgentSkillsHint: 'Vindt voor elke uitvoering passende Vaardigheden, net als in een chat.',
    newAgentUsesHint: 'Bedrijfskennis en de verbindingen die het mag gebruiken.',
    newAgentDeliversHint:
      'Een resultaat: bij elke uitvoering nieuw of één resultaat dat actueel wordt gehouden.',
    agentTab: 'Agent',
    runsTab: 'Uitvoeringen',
    versionsTab: 'Versies',
    testRunTab: 'Testrun',
    versionMeta: 'Versie {version}',
    changeProjectBriefPrompt: 'Wijzig de projectomschrijving:',
    changeProjectPrompt: 'Wijzig dit project:',
    changeAgentUsesPrompt:
      'Help me wijzigen wat agent {id} ({name}) gebruikt: de kennis, verbindingen en bronnen ervan. Lees eerst de huidige definitie.',
    makeChatProjectNamed: 'Maak van deze chat een project met de naam {name}.',
    makeChatProject: 'Maak van deze chat een project.',
    projectBriefInstruction: 'Gebruik dit als omschrijving:\n{brief}',
    couldntCreateProject: 'Het project kon niet worden aangemaakt.',
    treeToggle: "{action} de pagina's onder {title}",
    treeHide: 'Verbergen',
    treeShow: 'Tonen',
    goalHint: 'Wat moet deze agent bereiken?',
    instructionsHint: 'Hoe moet de agent het werk aanpakken en beslissingen nemen?',
    schedulePresetOff: 'Uit',
    schedulePresetHourly: 'Elk uur',
    schedulePresetDaily: 'Elke dag',
    schedulePresetWeekdays: 'Werkdagen',
    schedulePresetWeekly: 'Wekelijks',
    schedulePresetCustom: 'Aangepast',
    pageFallbackAnother: 'een andere pagina',
    pageFallbackOverview: 'het bedrijfsoverzicht',
    noteOptional: 'Notitie (optioneel)',
    whyThisChange: 'Waarom deze wijziging?',
    discardChangesConfirm: 'Je wijzigingen weggooien? Er is niets opgeslagen.',
    editAgentChatPrompt:
      'Help me agent {id} ({name}) te verbeteren. Lees eerst de huidige definitie.',
    revisionLabel: 'Revisie {number}',
    movePageTitle: 'Pagina “{title}” verplaatsen',
    deletePageTitle: '“{title}” permanent verwijderen?',
    archivePageTitle: '“{title}” archiveren?',
    suggestArchivePageTitle: 'Voorstellen om “{title}” te archiveren',
    editedLabel: 'Bewerkt',
    editedPageSummary: 'Pagina bewerkt: “{note}”',
    revisionConflict:
      'De pagina is gewijzigd terwijl je deze bekeek. De pagina is opnieuw geladen: beoordeel de suggestie opnieuw.',
  },
  et: {
    suggestionNewPageUnder: 'Uus leht „{title}” asukohta {parent}',
    suggestionMoveUnder: 'Teisalda see leht asukohta {parent}',
    suggestionArchive: 'Arhiveeri see leht',
    suggestionRenameTo: 'Nimeta see leht ümber nimega „{title}”',
    suggestionOverlapNote:
      'See muudab sama teksti nagu teine soovitus, seega kuvatakse selle muudatus siin, mitte lehel.',
    suggestionMismatchNote:
      'Selle muudatust ei saanud lehe praeguse sisu põhjal kuvada. Laadi leht uuesti, et see üle vaadata.',
    showChange: 'kuva see lehel',
    pageMove: 'Teisalda…',
    pageArchive: 'Arhiveeri',
    pageRestore: 'Taasta leht',
    pageDeletePermanently: 'Kustuta jäädavalt',
    pageExportMarkdown: 'Ekspordi Markdownina',
    pageActions: 'Lehe toimingud',
    pageMoveArchiveReason: 'Teisalda või arhiveeri esmalt selle all olevad lehed.',
    expectedResult: 'Oodatav tulemus',
    expectedResultHint: 'Mida edukas käitus peaks andma.',
    resources: 'Teadmised ja ühendused',
    resourcesHint:
      'Kust otsida. Iga käitus kasutab ainult ressursse, millele selle nimel tegutseval kasutajal on juurdepääs.',
    boundaries: 'Piirangud ja toimingud',
    boundariesHint:
      'Mida teha või vältida. Iga ühendatud toiming vajab endiselt inimese heakskiitu.',
    skillsOptional: 'Oskused · valikuline',
    skillsOptionalNote:
      'Korduvkasutatavad meetodid, mida see agent saab kasutada. See töötab ka ilma oskusteta.',
    notSpecified: 'Määramata',
    newResultEachRun: 'Iga käituse jaoks uus tulemus',
    keepResultUpdated: 'Hoia üks tulemus ajakohasena',
    whatEachRunDelivers: 'Mida iga käitus annab',
    editAccessRequested: 'Muutmisõigust taotletud · vastuse ootel.',
    askForEditAccess: 'Selle muutmiseks taotle muutmisõigust või loo enda koopia.',
    newAgentGoalHint: 'Mida see peaks saavutama.',
    newAgentScheduleHint: 'Nõudmisel või sinu valitud ajakava alusel.',
    newAgentInstructionsHint: 'Kuidas see peaks tööd tegema.',
    newAgentSkillsHint: 'Leiab iga käituse jaoks sobivad oskused samamoodi nagu vestlus.',
    newAgentUsesHint: 'Ettevõtte teadmised ja ühendused, mida see kasutada võib.',
    newAgentDeliversHint: 'Tulemus: iga käituse jaoks uus või üks ajakohasena hoitav tulemus.',
    agentTab: 'Agent',
    runsTab: 'Käitused',
    versionsTab: 'Versioonid',
    testRunTab: 'Testkäitus',
    versionMeta: 'Versioon {version}',
    changeProjectBriefPrompt: 'Muuda projekti lähteülesannet:',
    changeProjectPrompt: 'Muuda seda projekti:',
    changeAgentUsesPrompt:
      'Aita mul muuta seda, mida agent {id} ({name}) kasutab: oma teadmisi, ühendusi ja ressursse. Loe esmalt selle praegust määratlust.',
    makeChatProjectNamed: 'Muuda see vestlus projektiks nimega {name}.',
    makeChatProject: 'Muuda see vestlus projektiks.',
    projectBriefInstruction: 'Kasuta seda lähteülesannet:\n{brief}',
    couldntCreateProject: 'Projekti ei õnnestunud luua.',
    treeToggle: '{action} lehed jaotises {title}',
    treeHide: 'Peida',
    treeShow: 'Näita',
    goalHint: 'Mida peaks see agent saavutama?',
    instructionsHint: 'Kuidas peaks agent tööle lähenema ja otsuseid langetama.',
    schedulePresetOff: 'Väljas',
    schedulePresetHourly: 'Iga tund',
    schedulePresetDaily: 'Iga päev',
    schedulePresetWeekdays: 'Tööpäevadel',
    schedulePresetWeekly: 'Iga nädal',
    schedulePresetCustom: 'Kohandatud',
    pageFallbackAnother: 'teine leht',
    pageFallbackOverview: 'ettevõtte ülevaade',
    noteOptional: 'Märkus (valikuline)',
    whyThisChange: 'Miks seda muudatust teha?',
    discardChangesConfirm: 'Kas loobuda muudatustest? Midagi pole salvestatud.',
    editAgentChatPrompt:
      'Aidake mul agenti {id} ({name}) täiustada. Lugege esmalt selle praegust määratlust.',
    revisionLabel: 'Versioon {number}',
    movePageTitle: 'Teisalda „{title}”',
    deletePageTitle: 'Kas kustutada „{title}” jäädavalt?',
    archivePageTitle: 'Kas arhiveerida „{title}”?',
    suggestArchivePageTitle: 'Soovita „{title}” arhiveerimist',
    editedLabel: 'Muudetud',
    editedPageSummary: 'Lehte muudeti: „{note}“',
    revisionConflict:
      'Leht muutus selle ülevaatamise ajal. See laaditi uuesti: vaadake soovitus uuesti üle.',
  },
  fi: {
    suggestionMoveUnder: 'Siirrä tämä sivu kohteen {parent} alle',
    suggestionArchive: 'Arkistoi tämä sivu',
    suggestionRenameTo: 'Nimeä tämä sivu uudelleen nimellä ”{title}”',
    suggestionOverlapNote:
      'Se muuttaa samaa tekstiä kuin toinen ehdotus, joten sen muutos näytetään tässä sivun sijaan.',
    suggestionMismatchNote:
      'Sen muutosta ei voitu näyttää suhteessa sivun nykyiseen sisältöön. Lataa sivu uudelleen tarkistaaksesi sen.',
    showChange: 'näytä se sivulla',
    pageMove: 'Siirrä…',
    pageArchive: 'Arkistoi',
    pageRestore: 'Palauta sivu',
    pageDeletePermanently: 'Poista pysyvästi',
    pageExportMarkdown: 'Vie Markdownina',
    pageActions: 'Sivun toiminnot',
    pageMoveArchiveReason: 'Siirrä tai arkistoi ensin sen alla olevat sivut.',
    expectedResult: 'Odotettu tulos',
    expectedResultHint: 'Mitä onnistuneen suorituksen pitäisi tuottaa.',
    resources: 'Tietämys ja yhteydet',
    resourcesHint:
      'Mistä etsiä. Jokainen suoritus käyttää vain resursseja, joihin sen suorittavalla henkilöllä on pääsy.',
    boundaries: 'Rajaukset ja toiminnot',
    boundariesHint:
      'Mitä tehdä tai välttää. Jokainen yhdistetty toiminto vaatii edelleen ihmisen hyväksynnän.',
    skillsOptional: 'Taidot · valinnainen',
    skillsOptionalNote:
      'Uudelleenkäytettävät menetelmät, joita tämä agentti voi käyttää. Se toimii ilman Taitojakin.',
    notSpecified: 'Ei määritetty',
    newResultEachRun: 'Uusi tulos jokaisella suorituksella',
    keepResultUpdated: 'Pidä yksi tulos ajan tasalla',
    whatEachRunDelivers: 'Mitä kukin suoritus tuottaa',
    editAccessRequested: 'Muokkausoikeutta pyydetty · odotetaan vastausta.',
    askForEditAccess: 'Jos haluat muuttaa sitä, pyydä muokkausoikeutta tai tee oma kopio.',
    newAgentGoalHint: 'Mitä sen pitäisi saavuttaa.',
    newAgentScheduleHint: 'Tarvittaessa tai valitsemasi aikataulun mukaan.',
    newAgentInstructionsHint: 'Miten sen pitäisi tehdä työ.',
    newAgentSkillsHint:
      'Etsii kuhunkin suoritukseen sopivat Taidot samalla tavalla kuin keskustelu.',
    newAgentUsesHint: 'Yrityksen tietämys ja yhteydet, joita se voi käyttää.',
    newAgentDeliversHint: 'Tulos: uusi jokaisella suorituksella tai yksi ajan tasalla pidettävä.',
    agentTab: 'Agentti',
    runsTab: 'Suoritukset',
    versionsTab: 'Versiot',
    testRunTab: 'Testisuoritus',
    versionMeta: 'Versio {version}',
    changeProjectBriefPrompt: 'Muuta projektin kuvausta:',
    changeProjectPrompt: 'Muuta tätä projektia:',
    changeAgentUsesPrompt:
      'Auta minua muuttamaan sitä, mitä agentti {id} ({name}) käyttää: sen tietämystä, yhteyksiä ja resursseja. Lue ensin sen nykyinen määritys.',
    makeChatProjectNamed: 'Tee tästä keskustelusta projekti nimeltä {name}.',
    makeChatProject: 'Tee tästä keskustelusta projekti.',
    projectBriefInstruction: 'Käytä tätä kuvauksena:\n{brief}',
    couldntCreateProject: 'Projektin luominen epäonnistui.',
    suggestionNewPageUnder: 'Uusi sivu ”{title}” kohteen {parent} alle',
    treeToggle: '{action} {title}-kohdan sivut',
    treeHide: 'Piilota',
    treeShow: 'Näytä',
    goalHint: 'Mitä tämän agentin tulisi saavuttaa?',
    instructionsHint: 'Miten agentin tulisi lähestyä työtä ja tehdä päätöksiä.',
    schedulePresetOff: 'Pois käytöstä',
    schedulePresetHourly: 'Joka tunti',
    schedulePresetDaily: 'Joka päivä',
    schedulePresetWeekdays: 'Arkipäivät',
    schedulePresetWeekly: 'Viikoittain',
    schedulePresetCustom: 'Mukautettu',
    pageFallbackAnother: 'toinen sivu',
    pageFallbackOverview: 'yrityksen yleiskatsaus',
    noteOptional: 'Muistiinpano (valinnainen)',
    whyThisChange: 'Miksi tämä muutos?',
    discardChangesConfirm: 'Hylätäänkö tekemäsi muutokset? Mitään ei ole tallennettu.',
    editAgentChatPrompt:
      'Auta minua parantamaan agenttia {id} ({name}). Lue ensin sen nykyinen määritys.',
    revisionLabel: 'Versio {number}',
    movePageTitle: 'Siirrä ”{title}”',
    deletePageTitle: 'Poistetaanko ”{title}” pysyvästi?',
    archivePageTitle: 'Arkistoidaanko ”{title}”?',
    suggestArchivePageTitle: 'Ehdota ”{title}” arkistoitavaksi',
    editedLabel: 'Muokattu',
    editedPageSummary: 'Sivua muokattiin: “{note}”',
    revisionConflict:
      'Sivu muuttui tarkastelusi aikana. Se ladattiin uudelleen: tarkista ehdotus uudelleen.',
  },
  fr: {
    suggestionNewPageUnder: 'Nouvelle page « {title} » sous {parent}',
    suggestionMoveUnder: 'Déplacer cette page sous {parent}',
    suggestionArchive: 'Archiver cette page',
    suggestionRenameTo: 'Renommer cette page en « {title} »',
    suggestionOverlapNote:
      'Cette modification porte sur le même texte qu’une autre suggestion ; elle est donc affichée ici plutôt que dans la page.',
    suggestionMismatchNote:
      'Cette modification n’a pas pu être affichée par rapport au contenu actuel de la page. Rechargez la page pour l’examiner.',
    showChange: 'l’afficher dans la page',
    pageMove: 'Déplacer vers…',
    pageArchive: 'Archiver',
    pageRestore: 'Restaurer la page',
    pageDeletePermanently: 'Supprimer définitivement',
    pageExportMarkdown: 'Exporter en Markdown',
    pageActions: 'Actions de la page',
    pageMoveArchiveReason: 'Déplacez ou archivez d’abord les pages qu’elle contient.',
    expectedResult: 'Résultat attendu',
    expectedResultHint: 'Ce qu’une exécution réussie doit produire.',
    resources: 'Connaissances et connexions',
    resourcesHint:
      'Où chercher. Chaque exécution utilise uniquement les ressources auxquelles la personne qui l’exécute peut accéder.',
    boundaries: 'Limites et actions',
    boundariesHint:
      'Ce qu’il faut faire ou éviter. Toute action connectée nécessite toujours l’approbation d’une personne.',
    skillsOptional: 'Compétences · facultatif',
    skillsOptionalNote:
      'Méthodes réutilisables que cet agent peut utiliser. Il fonctionne sans aucune compétence.',
    notSpecified: 'Non spécifié',
    newResultEachRun: 'Un nouveau résultat à chaque exécution',
    keepResultUpdated: 'Garder un résultat à jour',
    whatEachRunDelivers: 'Ce que produit chaque exécution',
    editAccessRequested: 'Accès en modification demandé · en attente d’une réponse.',
    askForEditAccess:
      'Pour le modifier, demandez l’accès en modification ou créez votre propre copie.',
    newAgentGoalHint: 'Ce qu’il doit accomplir.',
    newAgentScheduleHint: 'À la demande ou selon une planification de votre choix.',
    newAgentInstructionsHint: 'Comment il doit effectuer le travail.',
    newAgentSkillsHint:
      'Il trouve les compétences adaptées à chaque exécution, comme le ferait un chat.',
    newAgentUsesHint: 'Les connaissances de l’entreprise et les connexions qu’il peut utiliser.',
    newAgentDeliversHint: 'Un résultat : nouveau à chaque exécution ou maintenu à jour.',
    agentTab: 'Agent',
    runsTab: 'Exécutions',
    versionsTab: 'Versions',
    testRunTab: 'Exécution de test',
    versionMeta: 'Version {version}',
    changeProjectBriefPrompt: 'Modifier le brief du projet :',
    changeProjectPrompt: 'Modifier ce projet :',
    changeAgentUsesPrompt:
      'Aidez-moi à modifier ce qu’utilise l’agent {id} ({name}) : ses connaissances, ses connexions et ses ressources. Lisez d’abord sa définition actuelle.',
    makeChatProjectNamed: 'Transformer cette conversation en projet nommé {name}.',
    makeChatProject: 'Transformer cette conversation en projet.',
    projectBriefInstruction: 'Utilisez ce brief :\n{brief}',
    couldntCreateProject: 'Impossible de créer le projet.',
    treeToggle: '{action} les pages sous {title}',
    treeHide: 'Masquer',
    treeShow: 'Afficher',
    goalHint: 'Que doit accomplir cet agent ?',
    instructionsHint: 'Comment l’agent doit-il aborder le travail et prendre des décisions ?',
    schedulePresetOff: 'Désactivé',
    schedulePresetHourly: 'Toutes les heures',
    schedulePresetDaily: 'Tous les jours',
    schedulePresetWeekdays: 'Jours ouvrés',
    schedulePresetWeekly: 'Chaque semaine',
    schedulePresetCustom: 'Personnalisé',
    pageFallbackAnother: 'une autre page',
    pageFallbackOverview: 'la présentation de l’entreprise',
    noteOptional: 'Note (facultatif)',
    whyThisChange: 'Pourquoi cette modification ?',
    discardChangesConfirm: 'Abandonner vos modifications ? Rien n’a été enregistré.',
    editAgentChatPrompt:
      'Aidez-moi à améliorer l’agent {id} ({name}). Lisez d’abord sa définition actuelle.',
    revisionLabel: 'Révision {number}',
    movePageTitle: 'Déplacer “{title}”',
    deletePageTitle: 'Supprimer définitivement “{title}” ?',
    archivePageTitle: 'Archiver “{title}” ?',
    suggestArchivePageTitle: 'Suggérer d’archiver “{title}”',
    editedLabel: 'Modifié',
    editedPageSummary: 'Page modifiée : « {note} »',
    revisionConflict:
      'La page a changé pendant que vous l’examiniez. Elle a été rechargée : examinez à nouveau la suggestion.',
  },
  de: {
    suggestionNewPageUnder: 'Neue Seite „{title}“ unter {parent}',
    suggestionMoveUnder: 'Diese Seite unter {parent} verschieben',
    suggestionArchive: 'Diese Seite archivieren',
    suggestionRenameTo: 'Diese Seite in „{title}“ umbenennen',
    suggestionOverlapNote:
      'Sie ändert denselben Text wie ein anderer Vorschlag. Daher wird ihre Änderung hier statt auf der Seite angezeigt.',
    suggestionMismatchNote:
      'Ihre Änderung konnte nicht anhand des aktuellen Seiteninhalts angezeigt werden. Laden Sie die Seite neu, um sie zu prüfen.',
    showChange: 'auf der Seite anzeigen',
    pageMove: 'Verschieben …',
    pageArchive: 'Archivieren',
    pageRestore: 'Seite wiederherstellen',
    pageDeletePermanently: 'Dauerhaft löschen',
    pageExportMarkdown: 'Markdown exportieren',
    pageActions: 'Seitenaktionen',
    pageMoveArchiveReason: 'Verschieben oder archivieren Sie zuerst die darunterliegenden Seiten.',
    expectedResult: 'Erwartetes Ergebnis',
    expectedResultHint: 'Was ein erfolgreicher Lauf liefern sollte.',
    resources: 'Wissen & Verbindungen',
    resourcesHint:
      'Wo gesucht werden soll. Jeder Lauf verwendet nur Ressourcen, auf die die handelnde Person Zugriff hat.',
    boundaries: 'Grenzen & Aktionen',
    boundariesHint:
      'Was zu tun oder zu vermeiden ist. Jede verbundene Aktion erfordert weiterhin die Zustimmung eines Menschen.',
    skillsOptional: 'Fähigkeiten · optional',
    skillsOptionalNote:
      'Wiederverwendbare Methoden, die dieser Agent nutzen kann. Er funktioniert auch ohne Fähigkeiten.',
    notSpecified: 'Nicht angegeben',
    newResultEachRun: 'Bei jedem Lauf ein neues Ergebnis',
    keepResultUpdated: 'Ein Ergebnis aktuell halten',
    whatEachRunDelivers: 'Was jeder Lauf liefert',
    editAccessRequested: 'Bearbeitungszugriff angefordert · auf Antwort warten.',
    askForEditAccess:
      'Um es zu ändern, bitten Sie um Bearbeitungszugriff oder erstellen Sie eine eigene Kopie.',
    newAgentGoalHint: 'Was es erreichen soll.',
    newAgentScheduleHint: 'Auf Abruf oder nach einem Zeitplan Ihrer Wahl.',
    newAgentInstructionsHint: 'Wie es die Aufgabe erledigen soll.',
    newAgentSkillsHint: 'Findet für jeden Lauf passende Fähigkeiten – genauso wie ein Chat.',
    newAgentUsesHint: 'Unternehmenswissen und die Verbindungen, die es nutzen darf.',
    newAgentDeliversHint:
      'Ein Ergebnis: bei jedem Lauf neu oder ein einziges, das aktuell gehalten wird.',
    agentTab: 'Agent',
    runsTab: 'Läufe',
    versionsTab: 'Versionen',
    testRunTab: 'Testlauf',
    versionMeta: 'Version {version}',
    changeProjectBriefPrompt: 'Projektbeschreibung ändern:',
    changeProjectPrompt: 'Dieses Projekt ändern:',
    changeAgentUsesPrompt:
      'Helfen Sie mir zu ändern, was Agent {id} ({name}) verwendet: sein Wissen, seine Verbindungen und seine Ressourcen. Lesen Sie zuerst seine aktuelle Definition.',
    makeChatProjectNamed: 'Diesen Chat zu einem Projekt namens {name} machen.',
    makeChatProject: 'Diesen Chat zu einem Projekt machen.',
    projectBriefInstruction: 'Verwenden Sie diese Beschreibung:\n{brief}',
    couldntCreateProject: 'Das Projekt konnte nicht erstellt werden.',
    treeToggle: '{action} die Seiten unter {title}',
    treeHide: 'Ausblenden',
    treeShow: 'Einblenden',
    goalHint: 'Was soll dieser Agent erreichen?',
    instructionsHint: 'Wie soll der Agent die Arbeit angehen und Entscheidungen treffen?',
    schedulePresetOff: 'Aus',
    schedulePresetHourly: 'Stündlich',
    schedulePresetDaily: 'Täglich',
    schedulePresetWeekdays: 'Wochentags',
    schedulePresetWeekly: 'Wöchentlich',
    schedulePresetCustom: 'Benutzerdefiniert',
    pageFallbackAnother: 'eine andere Seite',
    pageFallbackOverview: 'die Unternehmensübersicht',
    noteOptional: 'Notiz (optional)',
    whyThisChange: 'Warum diese Änderung?',
    discardChangesConfirm: 'Änderungen verwerfen? Es wurde nichts gespeichert.',
    editAgentChatPrompt:
      'Helfen Sie mir, den Agenten {id} ({name}) zu verbessern. Lesen Sie zuerst seine aktuelle Definition.',
    revisionLabel: 'Revision {number}',
    movePageTitle: '„{title}“ verschieben',
    deletePageTitle: '„{title}“ dauerhaft löschen?',
    archivePageTitle: '„{title}“ archivieren?',
    suggestArchivePageTitle: 'Vorschlagen, „{title}“ zu archivieren',
    editedLabel: 'Bearbeitet',
    editedPageSummary: 'Seite bearbeitet: „{note}“',
    revisionConflict:
      'Die Seite hat sich geändert, während Sie sie überprüft haben. Sie wurde neu geladen: Überprüfen Sie den Vorschlag erneut.',
  },
  el: {
    suggestionNewPageUnder: 'Νέα σελίδα «{title}» κάτω από {parent}',
    suggestionMoveUnder: 'Μετακίνηση αυτής της σελίδας κάτω από {parent}',
    suggestionArchive: 'Αρχειοθέτηση αυτής της σελίδας',
    suggestionRenameTo: 'Μετονομασία αυτής της σελίδας σε «{title}»',
    suggestionOverlapNote:
      'Αλλάζει το ίδιο κείμενο με μια άλλη πρόταση, επομένως η αλλαγή της εμφανίζεται εδώ αντί για τη σελίδα.',
    suggestionMismatchNote:
      'Η αλλαγή της δεν ήταν δυνατό να εμφανιστεί σε σχέση με τη σελίδα όπως είναι τώρα. Επαναφορτώστε τη σελίδα για να την ελέγξετε.',
    showChange: 'εμφάνισή της στη σελίδα',
    pageMove: 'Μετακίνηση σε…',
    pageArchive: 'Αρχειοθέτηση',
    pageRestore: 'Επαναφορά σελίδας',
    pageDeletePermanently: 'Οριστική διαγραφή',
    pageExportMarkdown: 'Εξαγωγή Markdown',
    pageActions: 'Ενέργειες σελίδας',
    pageMoveArchiveReason: 'Μετακινήστε ή αρχειοθετήστε πρώτα τις σελίδες που περιέχει.',
    expectedResult: 'Αναμενόμενο αποτέλεσμα',
    expectedResultHint: 'Τι πρέπει να παραδώσει μια επιτυχής εκτέλεση.',
    resources: 'Γνώση και συνδέσεις',
    resourcesHint:
      'Πού να αναζητήσει. Κάθε εκτέλεση χρησιμοποιεί μόνο πόρους στους οποίους έχει πρόσβαση το άτομο που την εκτελεί.',
    boundaries: 'Όρια και ενέργειες',
    boundariesHint:
      'Τι να κάνει ή να αποφεύγει. Κάθε συνδεδεμένη ενέργεια εξακολουθεί να χρειάζεται ανθρώπινη έγκριση.',
    skillsOptional: 'Δεξιότητες · προαιρετικό',
    skillsOptionalNote:
      'Επαναχρησιμοποιήσιμες μέθοδοι που μπορεί να χρησιμοποιεί αυτός ο πράκτορας. Λειτουργεί και χωρίς Δεξιότητες.',
    notSpecified: 'Δεν έχει καθοριστεί',
    newResultEachRun: 'Νέο Αποτέλεσμα σε κάθε εκτέλεση',
    keepResultUpdated: 'Διατήρηση ενός Αποτελέσματος ενημερωμένου',
    whatEachRunDelivers: 'Τι παραδίδει κάθε εκτέλεση',
    editAccessRequested: 'Ζητήθηκε πρόσβαση επεξεργασίας · αναμονή απάντησης.',
    askForEditAccess:
      'Για να το αλλάξετε, ζητήστε πρόσβαση επεξεργασίας ή δημιουργήστε το δικό σας αντίγραφο.',
    newAgentGoalHint: 'Τι πρέπει να πετύχει.',
    newAgentScheduleHint: 'Κατά παραγγελία ή βάσει προγράμματος που θα επιλέξετε.',
    newAgentInstructionsHint: 'Πώς πρέπει να κάνει τη δουλειά.',
    newAgentSkillsHint:
      'Βρίσκει τις Δεξιότητες που ταιριάζουν σε κάθε εκτέλεση, όπως ακριβώς μια συνομιλία.',
    newAgentUsesHint: 'Η εταιρική γνώση και οι συνδέσεις που μπορεί να χρησιμοποιεί.',
    newAgentDeliversHint: 'Ένα Αποτέλεσμα: νέο σε κάθε εκτέλεση ή ένα που διατηρείται ενημερωμένο.',
    agentTab: 'Πράκτορας',
    runsTab: 'Εκτελέσεις',
    versionsTab: 'Εκδόσεις',
    testRunTab: 'Δοκιμαστική εκτέλεση',
    versionMeta: 'Έκδοση {version}',
    changeProjectBriefPrompt: 'Αλλαγή της σύνοψης του έργου:',
    changeProjectPrompt: 'Αλλαγή αυτού του έργου:',
    changeAgentUsesPrompt:
      'Βοηθήστε με να αλλάξω τι χρησιμοποιεί ο πράκτορας {id} ({name}): τις γνώσεις, τις συνδέσεις και τους πόρους του. Διαβάστε πρώτα τον τρέχοντα ορισμό του.',
    makeChatProjectNamed: 'Μετατροπή αυτής της συνομιλίας σε έργο με όνομα {name}.',
    makeChatProject: 'Μετατροπή αυτής της συνομιλίας σε έργο.',
    projectBriefInstruction: 'Χρησιμοποιήστε αυτήν τη σύνοψη:\n{brief}',
    couldntCreateProject: 'Δεν ήταν δυνατή η δημιουργία του έργου.',
    treeToggle: '{action} τις σελίδες κάτω από {title}',
    treeHide: 'Απόκρυψη',
    treeShow: 'Εμφάνιση',
    goalHint: 'Τι πρέπει να επιτύχει αυτός ο πράκτορας;',
    instructionsHint:
      'Πώς πρέπει να προσεγγίζει την εργασία και να λαμβάνει αποφάσεις ο πράκτορας.',
    schedulePresetOff: 'Ανενεργό',
    schedulePresetHourly: 'Κάθε ώρα',
    schedulePresetDaily: 'Κάθε μέρα',
    schedulePresetWeekdays: 'Καθημερινές',
    schedulePresetWeekly: 'Εβδομαδιαία',
    schedulePresetCustom: 'Προσαρμοσμένο',
    pageFallbackAnother: 'άλλη σελίδα',
    pageFallbackOverview: 'η επισκόπηση της εταιρείας',
    noteOptional: 'Σημείωση (προαιρετικό)',
    whyThisChange: 'Γιατί αυτή η αλλαγή;',
    discardChangesConfirm: 'Να απορριφθούν οι αλλαγές σας; Δεν έχει αποθηκευτεί τίποτα.',
    editAgentChatPrompt:
      'Βοηθήστε με να βελτιώσω τον agent {id} ({name}). Διαβάστε πρώτα τον τρέχοντα ορισμό του.',
    revisionLabel: 'Έκδοση {number}',
    movePageTitle: 'Μετακίνηση του “{title}”',
    deletePageTitle: 'Να διαγραφεί οριστικά το “{title}”;',
    archivePageTitle: 'Να αρχειοθετηθεί το “{title}”;',
    suggestArchivePageTitle: 'Πρόταση αρχειοθέτησης του “{title}”',
    editedLabel: 'Επεξεργασμένο',
    editedPageSummary: 'Έγινε επεξεργασία της σελίδας: «{note}»',
    revisionConflict:
      'Η σελίδα άλλαξε ενώ την ελέγχατε. Έχει φορτωθεί ξανά: ελέγξτε ξανά την πρόταση.',
  },
  hu: {
    suggestionNewPageUnder: 'Új oldal „{title}” a következő alatt: {parent}',
    suggestionMoveUnder: 'Az oldal áthelyezése a következő alá: {parent}',
    suggestionArchive: 'Az oldal archiválása',
    suggestionRenameTo: 'Az oldal átnevezése erre: „{title}”',
    suggestionOverlapNote:
      'Ugyanazt a szöveget módosítja, mint egy másik javaslat, ezért a módosítása itt, nem pedig az oldalon jelenik meg.',
    suggestionMismatchNote:
      'A módosítása nem jeleníthető meg az oldal jelenlegi állapotához képest. Töltse újra az oldalt az áttekintéséhez.',
    showChange: 'megjelenítés az oldalon',
    pageMove: 'Áthelyezés…',
    pageArchive: 'Archiválás',
    pageRestore: 'Oldal visszaállítása',
    pageDeletePermanently: 'Végleges törlés',
    pageExportMarkdown: 'Markdown exportálása',
    pageActions: 'Oldalműveletek',
    pageMoveArchiveReason: 'Előbb helyezze át vagy archiválja az alatta lévő oldalakat.',
    expectedResult: 'Várt eredmény',
    expectedResultHint: 'Amit egy sikeres futtatásnak el kell érnie.',
    resources: 'Tudás és kapcsolatok',
    resourcesHint:
      'Hol keressen. Minden futtatás csak azokat az erőforrásokat használja, amelyekhez az azt végző személy hozzáfér.',
    boundaries: 'Korlátok és műveletek',
    boundariesHint:
      'Mit tegyen vagy kerüljön el. Minden kapcsolt művelethez továbbra is emberi jóváhagyás szükséges.',
    skillsOptional: 'Képességek · nem kötelező',
    skillsOptionalNote:
      'Újra felhasználható módszerek, amelyeket ez az ügynök használhat. Képességek nélkül is működik.',
    notSpecified: 'Nincs megadva',
    newResultEachRun: 'Új eredmény minden futtatáskor',
    keepResultUpdated: 'Egy eredmény folyamatos frissítése',
    whatEachRunDelivers: 'Mit eredményez az egyes futtatások során',
    editAccessRequested: 'Szerkesztési hozzáférés kérve · válaszra vár.',
    askForEditAccess:
      'A módosításhoz kérjen szerkesztési hozzáférést, vagy készítsen saját másolatot.',
    newAgentGoalHint: 'Mit kell elérnie.',
    newAgentScheduleHint: 'Igény szerint vagy az Ön által választott ütemezés szerint.',
    newAgentInstructionsHint: 'Hogyan végezze el a feladatot.',
    newAgentSkillsHint:
      'Minden futtatáshoz megtalálja a megfelelő képességeket, ugyanúgy, mint egy csevegés.',
    newAgentUsesHint: 'A vállalati tudás és a használható kapcsolatok.',
    newAgentDeliversHint:
      'Eredmény: új minden futtatáskor, vagy egyetlen, folyamatosan frissített eredmény.',
    agentTab: 'Ügynök',
    runsTab: 'Futtatások',
    versionsTab: 'Verziók',
    testRunTab: 'Tesztfuttatás',
    versionMeta: '{version}. verzió',
    changeProjectBriefPrompt: 'A projekt rövid leírásának módosítása:',
    changeProjectPrompt: 'A projekt módosítása:',
    changeAgentUsesPrompt:
      'Segíts módosítani, hogy mit használ a(z) {id} ({name}) ügynök: a tudását, kapcsolatait és erőforrásait. Először olvasd el a jelenlegi definícióját.',
    makeChatProjectNamed: 'Legyen ebből a csevegésből {name} nevű projekt.',
    makeChatProject: 'Legyen ebből a csevegésből projekt.',
    projectBriefInstruction: 'Ezt használd rövid leírásként:\n{brief}',
    couldntCreateProject: 'Nem sikerült létrehozni a projektet.',
    treeToggle: '{action} a(z) {title} alatti oldalakat',
    treeHide: 'Elrejtés',
    treeShow: 'Megjelenítés',
    goalHint: 'Mit érjen el ez az ügynök?',
    instructionsHint: 'Hogyan közelítse meg az ügynök a feladatot, és hogyan hozzon döntéseket.',
    schedulePresetOff: 'Kikapcsolva',
    schedulePresetHourly: 'Óránként',
    schedulePresetDaily: 'Naponta',
    schedulePresetWeekdays: 'Hétköznapokon',
    schedulePresetWeekly: 'Hetente',
    schedulePresetCustom: 'Egyéni',
    pageFallbackAnother: 'egy másik oldal',
    pageFallbackOverview: 'a vállalat áttekintése',
    noteOptional: 'Megjegyzés (opcionális)',
    whyThisChange: 'Miért van szükség erre a módosításra?',
    discardChangesConfirm: 'Elveti a módosításokat? Semmit sem mentett.',
    editAgentChatPrompt:
      'Segíts továbbfejleszteni a(z) {id} ({name}) ügynököt. Először olvasd el a jelenlegi definícióját.',
    revisionLabel: 'Módosítás {number}',
    movePageTitle: 'A(z) „{title}” áthelyezése',
    deletePageTitle: 'Véglegesen törli a(z) „{title}” oldalt?',
    archivePageTitle: 'Archiválja a(z) „{title}” oldalt?',
    suggestArchivePageTitle: 'Javasolja a(z) „{title}” archiválását',
    editedLabel: 'Szerkesztve',
    editedPageSummary: 'Az oldal szerkesztve: „{note}”',
    revisionConflict:
      'Az oldal megváltozott, miközben átnézte. Újra betöltöttük: tekintse át ismét a javaslatot.',
  },
  ga: {
    suggestionNewPageUnder: 'Leathanach nua “{title}” faoi {parent}',
    suggestionMoveUnder: 'Bog an leathanach seo faoi {parent}',
    suggestionArchive: 'Cuir an leathanach seo sa chartlann',
    suggestionRenameTo: 'Athainmnigh an leathanach seo go “{title}”',
    suggestionOverlapNote:
      'Athraíonn sé an téacs céanna le moladh eile, mar sin taispeántar a athrú anseo seachas ar an leathanach.',
    suggestionMismatchNote:
      'Níorbh fhéidir a athrú a thaispeáint i gcoinne an leagain reatha den leathanach. Athlódáil an leathanach lena athbhreithniú.',
    showChange: 'taispeáin é ar an leathanach',
    pageMove: 'Bog go…',
    pageArchive: 'Cuir sa chartlann',
    pageRestore: 'Athchóirigh an leathanach',
    pageDeletePermanently: 'Scrios go buan',
    pageExportMarkdown: 'Easpórtáil Markdown',
    pageActions: 'Gníomhartha leathanaigh',
    pageMoveArchiveReason: 'Bog nó cuir na leathanaigh faoina bhun sa chartlann ar dtús.',
    expectedResult: 'Toradh ionchais',
    expectedResultHint: 'An méid ba cheart do rith rathúil a chur ar fáil.',
    resources: 'Eolas agus naisc',
    resourcesHint:
      'Cá háit le breathnú. Ní úsáideann gach rith ach na hacmhainní a bhfuil rochtain ag an duine atá ag gníomhú thar a cheann orthu.',
    boundaries: 'Teorainneacha agus gníomhartha',
    boundariesHint:
      'Cad atá le déanamh nó le seachaint. Teastaíonn ceadú daonna fós le haghaidh gach gnímh nasctha.',
    skillsOptional: 'Scileanna · roghnach',
    skillsOptionalNote:
      'Modhanna in-athúsáidte is féidir leis an ngníomhaire seo a úsáid. Oibríonn sé gan aon Scileanna.',
    notSpecified: 'Gan sonrú',
    newResultEachRun: 'Toradh nua gach rith',
    keepResultUpdated: 'Coinnigh Toradh amháin cothrom le dáta',
    whatEachRunDelivers: 'An méid a chuireann gach rith ar fáil',
    editAccessRequested: 'Iarradh rochtain eagarthóireachta · ag fanacht le freagra.',
    askForEditAccess: 'Chun é a athrú, iarr rochtain eagarthóireachta nó déan do chóip féin.',
    newAgentGoalHint: 'An méid ba cheart dó a bhaint amach.',
    newAgentScheduleHint: 'Ar éileamh, nó de réir sceidil a roghnaíonn tú.',
    newAgentInstructionsHint: 'An chaoi ar cheart dó an obair a dhéanamh.',
    newAgentSkillsHint:
      'Aimsíonn sé na Scileanna a oireann do gach rith, ar an mbealach céanna le comhrá.',
    newAgentUsesHint: 'Eolas na cuideachta agus na naisc a bhféadfadh sé úsáid a bhaint astu.',
    newAgentDeliversHint: 'Toradh: ceann nua gach rith, nó ceann a choinnítear cothrom le dáta.',
    agentTab: 'Gníomhaire',
    runsTab: 'Ritheanna',
    versionsTab: 'Leaganacha',
    testRunTab: 'Rith thástála',
    versionMeta: 'Leagan {version}',
    changeProjectBriefPrompt: 'Athraigh achoimre an tionscadail:',
    changeProjectPrompt: 'Athraigh an tionscadal seo:',
    changeAgentUsesPrompt:
      'Cabhraigh liom an méid a úsáideann gníomhaire {id} ({name}) a athrú: a chuid eolais, naisc agus acmhainní. Léigh a shainmhíniú reatha ar dtús.',
    makeChatProjectNamed: 'Déan tionscadal den chomhrá seo darb ainm {name}.',
    makeChatProject: 'Déan tionscadal den chomhrá seo.',
    projectBriefInstruction: 'Úsáid é seo don achoimre:\n{brief}',
    couldntCreateProject: 'Níorbh fhéidir an tionscadal a chruthú.',
    treeToggle: '{action} na leathanaigh faoi {title}',
    treeHide: 'Folaigh',
    treeShow: 'Taispeáin',
    goalHint: 'Céard ba cheart don ghníomhaire seo a bhaint amach?',
    instructionsHint:
      'Cén chaoi ar cheart don ghníomhaire tabhairt faoin obair agus cinntí a dhéanamh.',
    schedulePresetOff: 'Múchta',
    schedulePresetHourly: 'Gach uair an chloig',
    schedulePresetDaily: 'Gach lá',
    schedulePresetWeekdays: 'Laethanta na seachtaine',
    schedulePresetWeekly: 'Gach seachtain',
    schedulePresetCustom: 'Saincheaptha',
    pageFallbackAnother: 'leathanach eile',
    pageFallbackOverview: 'forbhreathnú na cuideachta',
    noteOptional: 'Nóta (roghnach)',
    whyThisChange: 'Cén fáth an t-athrú seo?',
    discardChangesConfirm:
      'An bhfuil tú cinnte gur mian leat do chuid athruithe a chaitheamh i leataobh? Níl aon rud sábháilte.',
    editAgentChatPrompt:
      'Cabhraigh liom gníomhaire {id} ({name}) a fheabhsú. Léigh a shainmhíniú reatha ar dtús.',
    revisionLabel: 'Athbhreithniú {number}',
    movePageTitle: 'Bog “{title}”',
    deletePageTitle: 'Scrios “{title}” go buan?',
    archivePageTitle: 'Cartlannaigh “{title}”?',
    suggestArchivePageTitle: 'Mol “{title}” a chartlannú',
    editedLabel: 'Curtha in eagar',
    editedPageSummary: 'Cuireadh an leathanach in eagar: “{note}”',
    revisionConflict:
      'Athraíodh an leathanach agus tú á athbhreithniú. Athlódáladh é: déan athbhreithniú ar an moladh arís.',
  },
  it: {
    suggestionNewPageUnder: 'Nuova pagina “{title}” sotto {parent}',
    suggestionMoveUnder: 'Sposta questa pagina sotto {parent}',
    suggestionArchive: 'Archivia questa pagina',
    suggestionRenameTo: 'Rinomina questa pagina in “{title}”',
    suggestionOverlapNote:
      'Modifica lo stesso testo di un altro suggerimento, quindi la modifica viene mostrata qui invece che nella pagina.',
    suggestionMismatchNote:
      'La modifica non può essere mostrata rispetto al contenuto attuale della pagina. Ricarica la pagina per esaminarla.',
    showChange: 'mostrala nella pagina',
    pageMove: 'Sposta in…',
    pageArchive: 'Archivia',
    pageRestore: 'Ripristina pagina',
    pageDeletePermanently: 'Elimina definitivamente',
    pageExportMarkdown: 'Esporta in Markdown',
    pageActions: 'Azioni della pagina',
    pageMoveArchiveReason: 'Prima sposta o archivia le pagine contenute al suo interno.',
    expectedResult: 'Risultato previsto',
    expectedResultHint: "Ciò che dovrebbe produrre un'esecuzione riuscita.",
    resources: 'Conoscenze e connessioni',
    resourcesHint:
      'Dove cercare. Ogni esecuzione usa solo le risorse a cui la persona che la esegue può accedere.',
    boundaries: 'Limiti e azioni',
    boundariesHint:
      "Cosa fare o evitare. Ogni azione connessa richiede comunque l'approvazione di una persona.",
    skillsOptional: 'Competenze · facoltative',
    skillsOptionalNote:
      'Metodi riutilizzabili che questo agente può usare. Funziona anche senza Competenze.',
    notSpecified: 'Non specificato',
    newResultEachRun: 'Un nuovo Risultato a ogni esecuzione',
    keepResultUpdated: 'Mantieni aggiornato un unico Risultato',
    whatEachRunDelivers: 'Cosa produce ogni esecuzione',
    editAccessRequested: 'Accesso alla modifica richiesto · in attesa di risposta.',
    askForEditAccess: "Per modificarlo, chiedi l'accesso alla modifica o crea una tua copia.",
    newAgentGoalHint: 'Cosa dovrebbe ottenere.',
    newAgentScheduleHint: 'Su richiesta o secondo una pianificazione a tua scelta.',
    newAgentInstructionsHint: 'Come dovrebbe svolgere il lavoro.',
    newAgentSkillsHint: 'Trova le Competenze adatte a ogni esecuzione, proprio come fa una chat.',
    newAgentUsesHint: "Le conoscenze dell'azienda e le connessioni che può usare.",
    newAgentDeliversHint: 'Un Risultato: nuovo a ogni esecuzione oppure mantenuto aggiornato.',
    agentTab: 'Agente',
    runsTab: 'Esecuzioni',
    versionsTab: 'Versioni',
    testRunTab: 'Esecuzione di prova',
    versionMeta: 'Versione {version}',
    changeProjectBriefPrompt: 'Modifica il brief del progetto:',
    changeProjectPrompt: 'Modifica questo progetto:',
    changeAgentUsesPrompt:
      "Aiutami a modificare ciò che usa l'agente {id} ({name}): conoscenze, connessioni e risorse. Leggi prima la definizione attuale.",
    makeChatProjectNamed: 'Trasforma questa chat in un progetto chiamato {name}.',
    makeChatProject: 'Trasforma questa chat in un progetto.',
    projectBriefInstruction: 'Usa questo brief:\n{brief}',
    couldntCreateProject: 'Non è stato possibile creare il progetto.',
    treeToggle: '{action} le pagine sotto {title}',
    treeHide: 'Nascondi',
    treeShow: 'Mostra',
    goalHint: 'Che cosa dovrebbe ottenere questo agente?',
    instructionsHint: 'Come dovrebbe affrontare il lavoro e prendere decisioni.',
    schedulePresetOff: 'Disattivato',
    schedulePresetHourly: 'Ogni ora',
    schedulePresetDaily: 'Ogni giorno',
    schedulePresetWeekdays: 'Giorni feriali',
    schedulePresetWeekly: 'Settimanale',
    schedulePresetCustom: 'Personalizzato',
    pageFallbackAnother: "un'altra pagina",
    pageFallbackOverview: "la panoramica dell'azienda",
    noteOptional: 'Nota (facoltativa)',
    whyThisChange: 'Perché questa modifica?',
    discardChangesConfirm: 'Vuoi annullare le modifiche? Non è stato salvato nulla.',
    editAgentChatPrompt:
      "Aiutami a migliorare l'agente {id} ({name}). Leggi prima la definizione attuale.",
    revisionLabel: 'Revisione {number}',
    movePageTitle: 'Spostare “{title}”',
    deletePageTitle: 'Eliminare definitivamente “{title}”?',
    archivePageTitle: 'Archiviare “{title}”?',
    suggestArchivePageTitle: 'Suggerire di archiviare “{title}”',
    editedLabel: 'Modificato',
    editedPageSummary: 'Pagina modificata: “{note}”',
    revisionConflict:
      'La pagina è cambiata mentre la esaminavi. È stata ricaricata: esamina di nuovo il suggerimento.',
  },
  lv: {
    suggestionNewPageUnder: 'Jauna lapa “{title}” zem {parent}',
    suggestionMoveUnder: 'Pārvietot šo lapu zem {parent}',
    suggestionArchive: 'Arhivēt šo lapu',
    suggestionRenameTo: 'Pārdēvēt šo lapu par “{title}”',
    suggestionOverlapNote:
      'Tā maina to pašu tekstu, ko cits ieteikums, tāpēc šīs izmaiņas tiek rādītas šeit, nevis lapā.',
    suggestionMismatchNote:
      'Šīs izmaiņas nevarēja parādīt, salīdzinot ar pašreizējo lapas saturu. Atkārtoti ielādējiet lapu, lai tās pārskatītu.',
    showChange: 'rādīt to lapā',
    pageMove: 'Pārvietot uz…',
    pageArchive: 'Arhivēt',
    pageRestore: 'Atjaunot lapu',
    pageDeletePermanently: 'Neatgriezeniski dzēst',
    pageExportMarkdown: 'Eksportēt Markdown',
    pageActions: 'Lapas darbības',
    pageMoveArchiveReason: 'Vispirms pārvietojiet vai arhivējiet tajā esošās lapas.',
    expectedResult: 'Paredzamais rezultāts',
    expectedResultHint: 'Kas jāiegūst veiksmīgas izpildes rezultātā.',
    resources: 'Zināšanas un savienojumi',
    resourcesHint:
      'Kur meklēt. Katrā izpildē tiek izmantoti tikai tie resursi, kuriem ir piekļuve personai, kuras vārdā tas darbojas.',
    boundaries: 'Robežas un darbības',
    boundariesHint:
      'Ko darīt vai no kā izvairīties. Katrai savienotajai darbībai joprojām nepieciešams cilvēka apstiprinājums.',
    skillsOptional: 'Prasmes · neobligāti',
    skillsOptionalNote:
      'Atkārtoti izmantojamas metodes, ko šis aģents var izmantot. Tas darbojas arī bez prasmēm.',
    notSpecified: 'Nav norādīts',
    newResultEachRun: 'Jauns rezultāts katrā izpildē',
    keepResultUpdated: 'Atjaunināt vienu rezultātu',
    whatEachRunDelivers: 'Ko sniedz katra izpilde',
    editAccessRequested: 'Pieprasīta rediģēšanas piekļuve · gaida atbildi.',
    askForEditAccess:
      'Lai to mainītu, pieprasiet rediģēšanas piekļuvi vai izveidojiet savu kopiju.',
    newAgentGoalHint: 'Kas tam jāsasniedz.',
    newAgentScheduleHint: 'Pēc pieprasījuma vai jūsu izvēlētajā grafikā.',
    newAgentInstructionsHint: 'Kā tam jāveic darbs.',
    newAgentSkillsHint: 'Katrā izpildē atrod piemērotās prasmes tāpat kā tērzēšanā.',
    newAgentUsesHint: 'Uzņēmuma zināšanas un savienojumi, ko tas var izmantot.',
    newAgentDeliversHint:
      'Rezultāts: jauns katrā izpildē vai viens, kas tiek regulāri atjaunināts.',
    agentTab: 'Aģents',
    runsTab: 'Izpildes',
    versionsTab: 'Versijas',
    testRunTab: 'Testa izpilde',
    versionMeta: 'Versija {version}',
    changeProjectBriefPrompt: 'Mainīt projekta aprakstu:',
    changeProjectPrompt: 'Mainīt šo projektu:',
    changeAgentUsesPrompt:
      'Palīdzi man mainīt, ko izmanto aģents {id} ({name}): tā zināšanas, savienojumus un resursus. Vispirms izlasi tā pašreizējo definīciju.',
    makeChatProjectNamed: 'Pārvērst šo tērzēšanu par projektu ar nosaukumu {name}.',
    makeChatProject: 'Pārvērst šo tērzēšanu par projektu.',
    projectBriefInstruction: 'Izmantojiet šo kā aprakstu:\n{brief}',
    couldntCreateProject: 'Neizdevās izveidot projektu.',
    treeToggle: '{action} lapas zem {title}',
    treeHide: 'Paslēpt',
    treeShow: 'Rādīt',
    goalHint: 'Ko šim aģentam vajadzētu sasniegt?',
    instructionsHint: 'Kā aģentam veikt darbu un pieņemt lēmumus.',
    schedulePresetOff: 'Izslēgts',
    schedulePresetHourly: 'Katru stundu',
    schedulePresetDaily: 'Katru dienu',
    schedulePresetWeekdays: 'Darbdienas',
    schedulePresetWeekly: 'Katru nedēļu',
    schedulePresetCustom: 'Pielāgots',
    pageFallbackAnother: 'cita lapa',
    pageFallbackOverview: 'uzņēmuma pārskats',
    noteOptional: 'Piezīme (neobligāti)',
    whyThisChange: 'Kāpēc šīs izmaiņas?',
    discardChangesConfirm: 'Vai atmest veiktās izmaiņas? Nekas nav saglabāts.',
    editAgentChatPrompt:
      'Palīdzi man uzlabot aģentu {id} ({name}). Vispirms izlasi tā pašreizējo definīciju.',
    revisionLabel: 'Versija {number}',
    movePageTitle: 'Pārvietot “{title}”',
    deletePageTitle: 'Vai neatgriezeniski dzēst “{title}”?',
    archivePageTitle: 'Vai arhivēt “{title}”?',
    suggestArchivePageTitle: 'Ieteikt arhivēt “{title}”',
    editedLabel: 'Rediģēts',
    editedPageSummary: 'Lapa rediģēta: “{note}”',
    revisionConflict:
      'Lapa tika mainīta, kamēr to pārskatījāt. Tā ir atkārtoti ielādēta: pārskatiet ieteikumu vēlreiz.',
  },
  lt: {
    suggestionNewPageUnder: 'Naujas puslapis „{title}“ po {parent}',
    suggestionMoveUnder: 'Perkelti šį puslapį po {parent}',
    suggestionArchive: 'Archyvuoti šį puslapį',
    suggestionRenameTo: 'Pervadinti šį puslapį į „{title}“',
    suggestionOverlapNote:
      'Jis keičia tą patį tekstą kaip ir kitas pasiūlymas, todėl jo pakeitimas rodomas čia, o ne puslapyje.',
    suggestionMismatchNote:
      'Jo pakeitimo nepavyko parodyti pagal dabartinį puslapio turinį. Iš naujo įkelkite puslapį, kad jį peržiūrėtumėte.',
    showChange: 'rodyti jį puslapyje',
    pageMove: 'Perkelti…',
    pageArchive: 'Archyvuoti',
    pageRestore: 'Atkurti puslapį',
    pageDeletePermanently: 'Ištrinti visam laikui',
    pageExportMarkdown: 'Eksportuoti Markdown',
    pageActions: 'Puslapio veiksmai',
    pageMoveArchiveReason: 'Pirmiausia perkelkite arba suarchyvuokite po juo esančius puslapius.',
    expectedResult: 'Laukiamas rezultatas',
    expectedResultHint: 'Ką turėtų pateikti sėkmingas paleidimas.',
    resources: 'Žinios ir ryšiai',
    resourcesHint:
      'Kur ieškoti. Kiekvieno paleidimo metu naudojami tik ištekliai, prie kurių turi prieigą jį atliekantis asmuo.',
    boundaries: 'Ribos ir veiksmai',
    boundariesHint:
      'Ką daryti arba ko vengti. Kiekvienam prijungtam veiksmui vis tiek reikia žmogaus patvirtinimo.',
    skillsOptional: 'Įgūdžiai · neprivaloma',
    skillsOptionalNote:
      'Pakartotinai naudojami metodai, kuriuos gali naudoti šis agentas. Jis veikia ir be jokių įgūdžių.',
    notSpecified: 'Nenurodyta',
    newResultEachRun: 'Naujas rezultatas kiekvieną kartą paleidus',
    keepResultUpdated: 'Nuolat atnaujinti vieną rezultatą',
    whatEachRunDelivers: 'Ką pateikia kiekvienas paleidimas',
    editAccessRequested: 'Prašoma redagavimo prieigos · laukiama atsakymo.',
    askForEditAccess:
      'Norėdami jį pakeisti, paprašykite redagavimo prieigos arba pasidarykite savo kopiją.',
    newAgentGoalHint: 'Ką jis turėtų pasiekti.',
    newAgentScheduleHint: 'Pagal poreikį arba pagal jūsų pasirinktą tvarkaraštį.',
    newAgentInstructionsHint: 'Kaip jis turėtų atlikti darbą.',
    newAgentSkillsHint: 'Kiekvienam paleidimui randa tinkamus įgūdžius, kaip tai daro pokalbis.',
    newAgentUsesHint: 'Įmonės žinios ir ryšiai, kuriuos jis gali naudoti.',
    newAgentDeliversHint:
      'Rezultatas: naujas kiekvieną kartą paleidus arba vienas nuolat atnaujinamas.',
    agentTab: 'Agentas',
    runsTab: 'Paleidimai',
    versionsTab: 'Versijos',
    testRunTab: 'Bandomasis paleidimas',
    versionMeta: 'Versija {version}',
    changeProjectBriefPrompt: 'Pakeisti projekto aprašą:',
    changeProjectPrompt: 'Pakeisti šį projektą:',
    changeAgentUsesPrompt:
      'Padėk man pakeisti, ką naudoja agentas {id} ({name}): jo žinias, ryšius ir išteklius. Pirmiausia perskaityk dabartinį jo apibrėžimą.',
    makeChatProjectNamed: 'Paversti šį pokalbį projektu pavadinimu {name}.',
    makeChatProject: 'Paversti šį pokalbį projektu.',
    projectBriefInstruction: 'Naudok šį aprašą:\n{brief}',
    couldntCreateProject: 'Nepavyko sukurti projekto.',
    treeToggle: '{action} puslapius po {title}',
    treeHide: 'Slėpti',
    treeShow: 'Rodyti',
    goalHint: 'Ką turėtų pasiekti šis agentas?',
    instructionsHint: 'Kaip agentas turėtų atlikti darbą ir priimti sprendimus.',
    schedulePresetOff: 'Išjungta',
    schedulePresetHourly: 'Kas valandą',
    schedulePresetDaily: 'Kasdien',
    schedulePresetWeekdays: 'Darbo dienomis',
    schedulePresetWeekly: 'Kas savaitę',
    schedulePresetCustom: 'Pasirinktinis',
    pageFallbackAnother: 'kitas puslapis',
    pageFallbackOverview: 'įmonės apžvalga',
    noteOptional: 'Pastaba (nebūtina)',
    whyThisChange: 'Kodėl šis pakeitimas?',
    discardChangesConfirm: 'Atmesti pakeitimus? Nieko neišsaugota.',
    editAgentChatPrompt:
      'Padėk man patobulinti agentą {id} ({name}). Pirmiausia perskaityk dabartinį jo apibrėžimą.',
    revisionLabel: 'Versija {number}',
    movePageTitle: 'Perkelti „{title}“',
    deletePageTitle: 'Ištrinti „{title}“ visam laikui?',
    archivePageTitle: 'Archyvuoti „{title}“?',
    suggestArchivePageTitle: 'Siūlyti archyvuoti „{title}“',
    editedLabel: 'Redaguota',
    editedPageSummary: 'Puslapis redaguotas: „{note}“',
    revisionConflict:
      'Puslapis pasikeitė, kol jį peržiūrėjote. Jis įkeltas iš naujo: dar kartą peržiūrėkite pasiūlymą.',
  },
  mt: {
    suggestionNewPageUnder: 'Paġna ġdida “{title}” taħt {parent}',
    suggestionMoveUnder: 'Mexxi din il-paġna taħt {parent}',
    suggestionArchive: 'Arkivja din il-paġna',
    suggestionRenameTo: 'Semmi mill-ġdid din il-paġna bħala “{title}”',
    suggestionOverlapNote:
      'Tbiddel l-istess test bħal suġġeriment ieħor, għalhekk il-bidla tagħha tidher hawn minflok fil-paġna.',
    suggestionMismatchNote:
      'Il-bidla tagħha ma setgħetx tintwera skont kif tidher il-paġna issa. Erġa’ tella’ l-paġna biex tirrevediha.',
    showChange: 'uriha fil-paġna',
    pageMove: 'Mexxi lejn…',
    pageArchive: 'Arkivja',
    pageRestore: 'Irrestawra l-paġna',
    pageDeletePermanently: 'Ħassar b’mod permanenti',
    pageExportMarkdown: 'Esporta Markdown',
    pageActions: 'Azzjonijiet tal-paġna',
    pageMoveArchiveReason: 'L-ewwel mexxi jew arkivja l-paġni li jinsabu taħtha.',
    expectedResult: 'Riżultat mistenni',
    expectedResultHint: 'Dak li għandu jipproduċi eżekuzzjoni b’suċċess.',
    resources: 'Għarfien u konnessjonijiet',
    resourcesHint:
      'Fejn tfittex. Kull eżekuzzjoni tuża biss riżorsi li l-persuna li qed taġixxi għandha aċċess għalihom.',
    boundaries: 'Limiti u azzjonijiet',
    boundariesHint:
      'X’għandek tagħmel jew tevita. Kull azzjoni konnessa xorta teħtieġ approvazzjoni minn persuna.',
    skillsOptional: 'Ħiliet · mhux obbligatorji',
    skillsOptionalNote:
      'Metodi li jistgħu jerġgħu jintużaw u li dan l-aġent jista’ juża. Jaħdem mingħajr Ħiliet.',
    notSpecified: 'Mhux speċifikat',
    newResultEachRun: 'Riżultat ġdid f’kull eżekuzzjoni',
    keepResultUpdated: 'Żomm Riżultat wieħed aġġornat',
    whatEachRunDelivers: 'Dak li jipproduċi kull eżekuzzjoni',
    editAccessRequested: 'Intalab aċċess għall-editjar · qed tistenna tweġiba.',
    askForEditAccess: 'Biex tibdlu, itlob aċċess għall-editjar jew oħloq kopja tiegħek.',
    newAgentGoalHint: 'Dak li għandu jikseb.',
    newAgentScheduleHint: 'Meta jkun meħtieġ, jew skont skeda li tagħżel int.',
    newAgentInstructionsHint: 'Kif għandu jwettaq ix-xogħol.',
    newAgentSkillsHint: 'Isib il-Ħiliet li jaqblu ma’ kull eżekuzzjoni, bl-istess mod bħal chat.',
    newAgentUsesHint: 'Għarfien tal-kumpanija u l-konnessjonijiet li jista’ juża.',
    newAgentDeliversHint:
      'Riżultat: wieħed ġdid f’kull eżekuzzjoni, jew wieħed li jinżamm aġġornat.',
    agentTab: 'Aġent',
    runsTab: 'Eżekuzzjonijiet',
    versionsTab: 'Verżjonijiet',
    testRunTab: 'Eżekuzzjoni tat-test',
    versionMeta: 'Verżjoni {version}',
    changeProjectBriefPrompt: 'Ibdel il-brief tal-proġett:',
    changeProjectPrompt: 'Ibdel dan il-proġett:',
    changeAgentUsesPrompt:
      'Għinni nbiddel dak li juża l-aġent {id} ({name}): l-għarfien, il-konnessjonijiet u r-riżorsi tiegħu. L-ewwel aqra d-definizzjoni attwali tiegħu.',
    makeChatProjectNamed: 'Agħmel minn din iċ-chat proġett bl-isem {name}.',
    makeChatProject: 'Agħmel minn din iċ-chat proġett.',
    projectBriefInstruction: 'Uża dan bħala deskrizzjoni:\n{brief}',
    couldntCreateProject: 'Ma setax jinħoloq il-proġett.',
    treeToggle: '{action} il-paġni taħt {title}',
    treeHide: 'Aħbi',
    treeShow: 'Uri',
    goalHint: 'X’għandu jikseb dan l-aġent?',
    instructionsHint: 'Kif għandu jindirizza x-xogħol u jieħu d-deċiżjonijiet dan l-aġent.',
    schedulePresetOff: 'Mitfi',
    schedulePresetHourly: 'Kull siegħa',
    schedulePresetDaily: 'Kull jum',
    schedulePresetWeekdays: 'Ġranet tax-xogħol',
    schedulePresetWeekly: 'Kull ġimgħa',
    schedulePresetCustom: 'Personalizzat',
    pageFallbackAnother: 'paġna oħra',
    pageFallbackOverview: 'il-ħarsa ġenerali tal-kumpanija',
    noteOptional: 'Nota (mhux obbligatorja)',
    whyThisChange: 'Għaliex din il-bidla?',
    discardChangesConfirm: 'Tarmi l-bidliet tiegħek? Xejn ma ġie salvat.',
    editAgentChatPrompt:
      'Għinni ntejjeb l-aġent {id} ({name}). L-ewwel aqra d-definizzjoni attwali tiegħu.',
    revisionLabel: 'Reviżjoni {number}',
    movePageTitle: 'Mexxi “{title}”',
    deletePageTitle: 'Tħassar “{title}” b’mod permanenti?',
    archivePageTitle: 'Tarkivja “{title}”?',
    suggestArchivePageTitle: 'Issuġġerixxi l-arkivjar ta’ “{title}”',
    editedLabel: 'Editjat',
    editedPageSummary: 'Editja l-paġna: “{note}”',
    revisionConflict:
      'Il-paġna nbidlet waqt li kont qed tirrevediha. Ġiet mgħobbija mill-ġdid: irrevedi s-suġġeriment mill-ġdid.',
  },
  pl: {
    suggestionNewPageUnder: 'Nowa strona „{title}” pod {parent}',
    suggestionMoveUnder: 'Przenieś tę stronę pod {parent}',
    suggestionArchive: 'Zarchiwizuj tę stronę',
    suggestionRenameTo: 'Zmień nazwę tej strony na „{title}”',
    suggestionOverlapNote:
      'Ta sugestia zmienia ten sam tekst co inna sugestia, dlatego jej zmiana jest wyświetlana tutaj zamiast na stronie.',
    suggestionMismatchNote:
      'Nie można było wyświetlić tej zmiany w odniesieniu do obecnej treści strony. Odśwież stronę, aby ją przejrzeć.',
    showChange: 'pokaż ją na stronie',
    pageMove: 'Przenieś do…',
    pageArchive: 'Archiwizuj',
    pageRestore: 'Przywróć stronę',
    pageDeletePermanently: 'Usuń trwale',
    pageExportMarkdown: 'Eksportuj Markdown',
    pageActions: 'Działania na stronie',
    pageMoveArchiveReason: 'Najpierw przenieś lub zarchiwizuj strony znajdujące się pod nią.',
    expectedResult: 'Oczekiwany wynik',
    expectedResultHint: 'Co powinno zostać dostarczone po pomyślnym uruchomieniu.',
    resources: 'Wiedza i połączenia',
    resourcesHint:
      'Gdzie szukać. Każde uruchomienie korzysta wyłącznie z zasobów, do których ma dostęp osoba wykonująca działanie.',
    boundaries: 'Zakres i działania',
    boundariesHint:
      'Co robić lub czego unikać. Każde połączone działanie nadal wymaga zatwierdzenia przez człowieka.',
    skillsOptional: 'Umiejętności · opcjonalnie',
    skillsOptionalNote:
      'Wielokrotnego użytku metody, z których ten agent może korzystać. Działa także bez żadnych umiejętności.',
    notSpecified: 'Nie określono',
    newResultEachRun: 'Nowy wynik przy każdym uruchomieniu',
    keepResultUpdated: 'Aktualizuj jeden wynik',
    whatEachRunDelivers: 'Co dostarcza każde uruchomienie',
    editAccessRequested: 'Poproszono o dostęp do edycji · oczekiwanie na odpowiedź.',
    askForEditAccess: 'Aby wprowadzić zmiany, poproś o dostęp do edycji lub utwórz własną kopię.',
    newAgentGoalHint: 'Co powinien osiągnąć.',
    newAgentScheduleHint: 'Na żądanie lub według wybranego harmonogramu.',
    newAgentInstructionsHint: 'Jak powinien wykonać pracę.',
    newAgentSkillsHint:
      'Znajduje umiejętności pasujące do każdego uruchomienia, tak samo jak czat.',
    newAgentUsesHint: 'Wiedza firmowa i połączenia, z których może korzystać.',
    newAgentDeliversHint:
      'Wynik: nowy przy każdym uruchomieniu lub jeden na bieżąco aktualizowany.',
    agentTab: 'Agent',
    runsTab: 'Uruchomienia',
    versionsTab: 'Wersje',
    testRunTab: 'Uruchomienie testowe',
    versionMeta: 'Wersja {version}',
    changeProjectBriefPrompt: 'Zmień opis projektu:',
    changeProjectPrompt: 'Zmień ten projekt:',
    changeAgentUsesPrompt:
      'Pomóż mi zmienić to, z czego korzysta agent {id} ({name}): jego wiedzę, połączenia i zasoby. Najpierw zapoznaj się z jego bieżącą definicją.',
    makeChatProjectNamed: 'Utwórz z tego czatu projekt o nazwie {name}.',
    makeChatProject: 'Utwórz z tego czatu projekt.',
    projectBriefInstruction: 'Użyj tego jako opisu:\n{brief}',
    couldntCreateProject: 'Nie udało się utworzyć projektu.',
    treeToggle: '{action} strony pod {title}',
    treeHide: 'Ukryj',
    treeShow: 'Pokaż',
    goalHint: 'Co ten agent powinien osiągnąć?',
    instructionsHint: 'Jak agent powinien podchodzić do pracy i podejmować decyzje.',
    schedulePresetOff: 'Wył.',
    schedulePresetHourly: 'Co godzinę',
    schedulePresetDaily: 'Codziennie',
    schedulePresetWeekdays: 'Dni robocze',
    schedulePresetWeekly: 'Co tydzień',
    schedulePresetCustom: 'Niestandardowy',
    pageFallbackAnother: 'inna strona',
    pageFallbackOverview: 'przegląd firmy',
    noteOptional: 'Notatka (opcjonalnie)',
    whyThisChange: 'Dlaczego ta zmiana?',
    discardChangesConfirm: 'Odrzucić zmiany? Nic nie zostało zapisane.',
    editAgentChatPrompt:
      'Pomóż mi ulepszyć agenta {id} ({name}). Najpierw przeczytaj jego bieżącą definicję.',
    revisionLabel: 'Wersja {number}',
    movePageTitle: 'Przenieść „{title}”',
    deletePageTitle: 'Usunąć „{title}” trwale?',
    archivePageTitle: 'Zarchiwizować „{title}”?',
    suggestArchivePageTitle: 'Zasugerować archiwizację „{title}”',
    editedLabel: 'Edytowano',
    editedPageSummary: 'Edytowano stronę: „{note}”',
    revisionConflict:
      'Strona zmieniła się podczas jej sprawdzania. Została ponownie załadowana — sprawdź sugestię jeszcze raz.',
  },
  pt: {
    suggestionNewPageUnder: 'Nova página “{title}” em {parent}',
    suggestionMoveUnder: 'Mover esta página para {parent}',
    suggestionArchive: 'Arquivar esta página',
    suggestionRenameTo: 'Renomear esta página para “{title}”',
    suggestionOverlapNote:
      'Ela altera o mesmo texto que outra sugestão, então a alteração é mostrada aqui em vez de na página.',
    suggestionMismatchNote:
      'Não foi possível mostrar a alteração em relação à página como ela está agora. Recarregue a página para analisá-la.',
    showChange: 'mostrar na página',
    pageMove: 'Mover para…',
    pageArchive: 'Arquivar',
    pageRestore: 'Restaurar página',
    pageDeletePermanently: 'Excluir permanentemente',
    pageExportMarkdown: 'Exportar Markdown',
    pageActions: 'Ações da página',
    pageMoveArchiveReason: 'Mova ou arquive primeiro as páginas que estão dentro dela.',
    expectedResult: 'Resultado esperado',
    expectedResultHint: 'O que uma execução bem-sucedida deve entregar.',
    resources: 'Conhecimento e conexões',
    resourcesHint:
      'Onde procurar. Cada execução usa apenas recursos aos quais a pessoa que a executa tem acesso.',
    boundaries: 'Limites e ações',
    boundariesHint: 'O que fazer ou evitar. Toda ação conectada ainda precisa de aprovação humana.',
    skillsOptional: 'Habilidades · opcional',
    skillsOptionalNote:
      'Métodos reutilizáveis que este agente pode usar. Ele funciona sem nenhuma Habilidade.',
    notSpecified: 'Não especificado',
    newResultEachRun: 'Um novo Resultado a cada execução',
    keepResultUpdated: 'Manter um Resultado atualizado',
    whatEachRunDelivers: 'O que cada execução entrega',
    editAccessRequested: 'Acesso para editar solicitado · aguardando resposta.',
    askForEditAccess: 'Para alterá-lo, solicite acesso para editar ou faça sua própria cópia.',
    newAgentGoalHint: 'O que deve alcançar.',
    newAgentScheduleHint: 'Sob demanda ou conforme uma programação escolhida.',
    newAgentInstructionsHint: 'Como deve realizar o trabalho.',
    newAgentSkillsHint:
      'Encontra as Habilidades adequadas para cada execução, da mesma forma que um chat.',
    newAgentUsesHint: 'Conhecimento da empresa e as conexões que pode usar.',
    newAgentDeliversHint: 'Um Resultado: novo a cada execução ou mantido atualizado.',
    agentTab: 'Agente',
    runsTab: 'Execuções',
    versionsTab: 'Versões',
    testRunTab: 'Execução de teste',
    versionMeta: 'Versão {version}',
    changeProjectBriefPrompt: 'Alterar o briefing do projeto:',
    changeProjectPrompt: 'Alterar este projeto:',
    changeAgentUsesPrompt:
      'Ajude-me a alterar o que o agente {id} ({name}) usa: conhecimento, conexões e recursos. Leia primeiro a definição atual.',
    makeChatProjectNamed: 'Transformar esta conversa em um projeto chamado {name}.',
    makeChatProject: 'Transformar esta conversa em um projeto.',
    projectBriefInstruction: 'Use isto para o briefing:\n{brief}',
    couldntCreateProject: 'Não foi possível criar o projeto.',
    treeToggle: '{action} as páginas sob {title}',
    treeHide: 'Ocultar',
    treeShow: 'Mostrar',
    goalHint: 'O que este agente deve alcançar?',
    instructionsHint: 'Como o agente deve realizar o trabalho e tomar decisões.',
    schedulePresetOff: 'Desativado',
    schedulePresetHourly: 'A cada hora',
    schedulePresetDaily: 'Todos os dias',
    schedulePresetWeekdays: 'Dias úteis',
    schedulePresetWeekly: 'Semanalmente',
    schedulePresetCustom: 'Personalizado',
    pageFallbackAnother: 'outra página',
    pageFallbackOverview: 'a visão geral da empresa',
    noteOptional: 'Observação (opcional)',
    whyThisChange: 'Por que esta alteração?',
    discardChangesConfirm: 'Descartar suas alterações? Nada foi salvo.',
    editAgentChatPrompt:
      'Ajude-me a melhorar o agente {id} ({name}). Leia primeiro a definição atual dele.',
    revisionLabel: 'Revisão {number}',
    movePageTitle: 'Mover “{title}”',
    deletePageTitle: 'Excluir “{title}” permanentemente?',
    archivePageTitle: 'Arquivar “{title}”?',
    suggestArchivePageTitle: 'Sugerir arquivamento de “{title}”',
    editedLabel: 'Editado',
    editedPageSummary: 'Página editada: “{note}”',
    revisionConflict:
      'A página foi alterada enquanto você a revisava. Ela foi recarregada: revise a sugestão novamente.',
  },
  ro: {
    suggestionNewPageUnder: 'Pagină nouă „{title}” sub {parent}',
    suggestionMoveUnder: 'Mută această pagină sub {parent}',
    suggestionArchive: 'Arhivează această pagină',
    suggestionRenameTo: 'Redenumește această pagină în „{title}”',
    suggestionOverlapNote:
      'Modifică același text ca o altă sugestie, așa că modificarea acesteia este afișată aici, nu în pagină.',
    suggestionMismatchNote:
      'Modificarea nu a putut fi afișată în raport cu versiunea actuală a paginii. Reîncarcă pagina pentru a o verifica.',
    showChange: 'afișeaz-o în pagină',
    pageMove: 'Mută în…',
    pageArchive: 'Arhivează',
    pageRestore: 'Restabilește pagina',
    pageDeletePermanently: 'Șterge definitiv',
    pageExportMarkdown: 'Exportă Markdown',
    pageActions: 'Acțiuni pentru pagină',
    pageMoveArchiveReason: 'Mai întâi mută sau arhivează paginile aflate sub ea.',
    expectedResult: 'Rezultat așteptat',
    expectedResultHint: 'Ce ar trebui să ofere o rulare reușită.',
    resources: 'Cunoștințe și conexiuni',
    resourcesHint:
      'Unde să caute. Fiecare rulare folosește doar resursele la care are acces utilizatorul care o declanșează.',
    boundaries: 'Limite și acțiuni',
    boundariesHint:
      'Ce să facă sau să evite. Orice acțiune conectată necesită în continuare aprobarea unei persoane.',
    skillsOptional: 'Abilități · opțional',
    skillsOptionalNote:
      'Metode reutilizabile pe care acest agent le poate folosi. Funcționează și fără Abilități.',
    notSpecified: 'Nespecificat',
    newResultEachRun: 'Un Rezultat nou la fiecare rulare',
    keepResultUpdated: 'Menține un singur Rezultat actualizat',
    whatEachRunDelivers: 'Ce oferă fiecare rulare',
    editAccessRequested: 'Accesul pentru editare a fost solicitat · se așteaptă un răspuns.',
    askForEditAccess:
      'Pentru a o modifica, solicită acces pentru editare sau creează-ți propria copie.',
    newAgentGoalHint: 'Ce ar trebui să realizeze.',
    newAgentScheduleHint: 'La cerere sau conform unui program ales de tine.',
    newAgentInstructionsHint: 'Cum ar trebui să efectueze activitatea.',
    newAgentSkillsHint:
      'Găsește Abilitățile potrivite pentru fiecare rulare, la fel ca într-un chat.',
    newAgentUsesHint: 'Cunoștințele companiei și conexiunile pe care le poate folosi.',
    newAgentDeliversHint: 'Un Rezultat: nou la fiecare rulare sau unul menținut la zi.',
    agentTab: 'Agent',
    runsTab: 'Rulări',
    versionsTab: 'Versiuni',
    testRunTab: 'Rulare de test',
    versionMeta: 'Versiunea {version}',
    changeProjectBriefPrompt: 'Modifică descrierea proiectului:',
    changeProjectPrompt: 'Modifică acest proiect:',
    changeAgentUsesPrompt:
      'Ajută-mă să modific ce folosește agentul {id} ({name}): cunoștințele, conexiunile și resursele sale. Citește mai întâi definiția actuală.',
    makeChatProjectNamed: 'Transformă acest chat într-un proiect numit {name}.',
    makeChatProject: 'Transformă acest chat într-un proiect.',
    projectBriefInstruction: 'Folosește această descriere:\n{brief}',
    couldntCreateProject: 'Proiectul nu a putut fi creat.',
    treeToggle: '{action} paginile de sub {title}',
    treeHide: 'Ascunde',
    treeShow: 'Afișează',
    goalHint: 'Ce ar trebui să realizeze acest agent?',
    instructionsHint: 'Cum ar trebui agentul să abordeze activitatea și să ia decizii.',
    schedulePresetOff: 'Dezactivat',
    schedulePresetHourly: 'În fiecare oră',
    schedulePresetDaily: 'În fiecare zi',
    schedulePresetWeekdays: 'În zilele lucrătoare',
    schedulePresetWeekly: 'Săptămânal',
    schedulePresetCustom: 'Personalizat',
    pageFallbackAnother: 'o altă pagină',
    pageFallbackOverview: 'prezentarea generală a companiei',
    noteOptional: 'Notă (opțional)',
    whyThisChange: 'De ce această modificare?',
    discardChangesConfirm: 'Renunțați la modificări? Nimic nu a fost salvat.',
    editAgentChatPrompt:
      'Ajută-mă să îmbunătățesc agentul {id} ({name}). Citește mai întâi definiția sa actuală.',
    revisionLabel: 'Revizie {number}',
    movePageTitle: 'Mută „{title}”',
    deletePageTitle: 'Ștergi definitiv „{title}”?',
    archivePageTitle: 'Arhivezi „{title}”?',
    suggestArchivePageTitle: 'Sugerează arhivarea „{title}”',
    editedLabel: 'Editat',
    editedPageSummary: 'Pagina a fost editată: „{note}”',
    revisionConflict:
      'Pagina s-a modificat în timp ce o examinați. A fost reîncărcată: examinați din nou sugestia.',
  },
  sk: {
    suggestionNewPageUnder: 'Nová stránka „{title}“ pod stránkou {parent}',
    suggestionMoveUnder: 'Presunúť túto stránku pod stránku {parent}',
    suggestionArchive: 'Archivovať túto stránku',
    suggestionRenameTo: 'Premenovať túto stránku na „{title}“',
    suggestionOverlapNote:
      'Mení rovnaký text ako iný návrh, preto sa jeho zmena zobrazuje tu namiesto na stránke.',
    suggestionMismatchNote:
      'Jeho zmenu sa nepodarilo zobraziť oproti aktuálnemu obsahu stránky. Obnovte stránku a skontrolujte ju.',
    showChange: 'zobraziť ju na stránke',
    pageMove: 'Presunúť…',
    pageArchive: 'Archivovať',
    pageRestore: 'Obnoviť stránku',
    pageDeletePermanently: 'Natrvalo odstrániť',
    pageExportMarkdown: 'Exportovať Markdown',
    pageActions: 'Akcie stránky',
    pageMoveArchiveReason:
      'Najskôr presuňte alebo archivujte stránky, ktoré sa pod ňou nachádzajú.',
    expectedResult: 'Očakávaný výsledok',
    expectedResultHint: 'Čo má úspešné spustenie priniesť.',
    resources: 'Znalosti a pripojenia',
    resourcesHint:
      'Kde hľadať. Každé spustenie používa iba zdroje, ku ktorým má prístup osoba, ktorá ho vykonáva.',
    boundaries: 'Hranice a akcie',
    boundariesHint:
      'Čo robiť alebo čomu sa vyhnúť. Každá pripojená akcia stále vyžaduje schválenie človekom.',
    skillsOptional: 'Zručnosti · voliteľné',
    skillsOptionalNote:
      'Opakovane použiteľné metódy, ktoré môže tento agent používať. Funguje aj bez akýchkoľvek zručností.',
    notSpecified: 'Neuvedené',
    newResultEachRun: 'Nový výsledok pri každom spustení',
    keepResultUpdated: 'Udržiavať jeden výsledok aktuálny',
    whatEachRunDelivers: 'Čo prináša každé spustenie',
    editAccessRequested: 'Prístup na úpravy vyžiadaný · čaká sa na odpoveď.',
    askForEditAccess:
      'Ak to chcete zmeniť, požiadajte o prístup na úpravy alebo si vytvorte vlastnú kópiu.',
    newAgentGoalHint: 'Čo má dosiahnuť.',
    newAgentScheduleHint: 'Na požiadanie alebo podľa vami zvoleného rozvrhu.',
    newAgentInstructionsHint: 'Ako má prácu vykonať.',
    newAgentSkillsHint: 'Pre každé spustenie nájde vhodné zručnosti, rovnako ako chat.',
    newAgentUsesHint: 'Firemné znalosti a pripojenia, ktoré môže používať.',
    newAgentDeliversHint: 'Výsledok: nový pri každom spustení alebo jeden priebežne aktualizovaný.',
    agentTab: 'Agent',
    runsTab: 'Spustenia',
    versionsTab: 'Verzie',
    testRunTab: 'Testovacie spustenie',
    versionMeta: 'Verzia {version}',
    changeProjectBriefPrompt: 'Zmeniť zadanie projektu:',
    changeProjectPrompt: 'Zmeniť tento projekt:',
    changeAgentUsesPrompt:
      'Pomôž mi zmeniť, čo agent {id} ({name}) používa: jeho znalosti, pripojenia a zdroje. Najskôr si prečítaj jeho aktuálnu definíciu.',
    makeChatProjectNamed: 'Vytvoriť z tohto chatu projekt s názvom {name}.',
    makeChatProject: 'Vytvoriť z tohto chatu projekt.',
    projectBriefInstruction: 'Použi toto zadanie:\n{brief}',
    couldntCreateProject: 'Projekt sa nepodarilo vytvoriť.',
    treeToggle: '{action} stránky pod {title}',
    treeHide: 'Skryť',
    treeShow: 'Zobraziť',
    goalHint: 'Čo má tento agent dosiahnuť?',
    instructionsHint: 'Ako má agent pristupovať k práci a prijímať rozhodnutia.',
    schedulePresetOff: 'Vypnuté',
    schedulePresetHourly: 'Každú hodinu',
    schedulePresetDaily: 'Každý deň',
    schedulePresetWeekdays: 'Pracovné dni',
    schedulePresetWeekly: 'Týždenne',
    schedulePresetCustom: 'Vlastné',
    pageFallbackAnother: 'inú stránku',
    pageFallbackOverview: 'prehľad spoločnosti',
    noteOptional: 'Poznámka (voliteľné)',
    whyThisChange: 'Prečo táto zmena?',
    discardChangesConfirm: 'Zahodiť zmeny? Nič sa neuložilo.',
    editAgentChatPrompt:
      'Pomôž mi vylepšiť agenta {id} ({name}). Najprv si prečítaj jeho aktuálnu definíciu.',
    revisionLabel: 'Revízia {number}',
    movePageTitle: 'Presunúť „{title}“',
    deletePageTitle: 'Odstrániť „{title}“ natrvalo?',
    archivePageTitle: 'Archivovať „{title}“?',
    suggestArchivePageTitle: 'Navrhnúť archiváciu „{title}“',
    editedLabel: 'Upravené',
    editedPageSummary: 'Upravili ste stránku: „{note}“',
    revisionConflict:
      'Stránka sa počas kontroly zmenila. Znova sa načítala: skontrolujte návrh ešte raz.',
  },
  sl: {
    suggestionNewPageUnder: 'Nova stran »{title}« pod {parent}',
    suggestionMoveUnder: 'Premakni to stran pod {parent}',
    suggestionArchive: 'Arhiviraj to stran',
    suggestionRenameTo: 'Preimenuj to stran v »{title}«',
    suggestionOverlapNote:
      'Spreminja isto besedilo kot drug predlog, zato je njegova sprememba prikazana tukaj namesto na strani.',
    suggestionMismatchNote:
      'Spremembe ni bilo mogoče prikazati glede na trenutno vsebino strani. Znova naloži stran, da jo pregledaš.',
    showChange: 'prikaži jo na strani',
    pageMove: 'Premakni v …',
    pageArchive: 'Arhiviraj',
    pageRestore: 'Obnovi stran',
    pageDeletePermanently: 'Trajno izbriši',
    pageExportMarkdown: 'Izvozi Markdown',
    pageActions: 'Dejanja strani',
    pageMoveArchiveReason: 'Najprej premakni ali arhiviraj strani pod njo.',
    expectedResult: 'Pričakovani rezultat',
    expectedResultHint: 'Kaj naj prinese uspešen zagon.',
    resources: 'Znanje in povezave',
    resourcesHint:
      'Kje iskati. Vsak zagon uporablja samo vire, do katerih ima dostop oseba, ki ga izvaja.',
    boundaries: 'Omejitve in dejanja',
    boundariesHint:
      'Kaj storiti ali čemu se izogniti. Za vsako povezano dejanje je še vedno potrebna odobritev človeka.',
    skillsOptional: 'Veščine · izbirno',
    skillsOptionalNote:
      'Ponovno uporabne metode, ki jih lahko ta agent uporablja. Deluje tudi brez veščin.',
    notSpecified: 'Ni določeno',
    newResultEachRun: 'Nov rezultat ob vsakem zagonu',
    keepResultUpdated: 'En rezultat, ki se sproti posodablja',
    whatEachRunDelivers: 'Kaj prinese vsak zagon',
    editAccessRequested: 'Zahtevan je dostop za urejanje · čaka na odgovor.',
    askForEditAccess:
      'Če ga želiš spremeniti, zaprosi za dostop za urejanje ali ustvari svojo kopijo.',
    newAgentGoalHint: 'Kaj naj doseže.',
    newAgentScheduleHint: 'Na zahtevo ali po urniku, ki ga izbereš.',
    newAgentInstructionsHint: 'Kako naj opravi delo.',
    newAgentSkillsHint: 'Za vsak zagon poišče ustrezne veščine, tako kot klepet.',
    newAgentUsesHint: 'Znanje podjetja ter povezave, ki jih lahko uporablja.',
    newAgentDeliversHint: 'Rezultat: nov ob vsakem zagonu ali tak, ki se sproti posodablja.',
    agentTab: 'Agent',
    runsTab: 'Zagoni',
    versionsTab: 'Različice',
    testRunTab: 'Preizkusni zagon',
    versionMeta: 'Različica {version}',
    changeProjectBriefPrompt: 'Spremeni povzetek projekta:',
    changeProjectPrompt: 'Spremeni ta projekt:',
    changeAgentUsesPrompt:
      'Pomagaj mi spremeniti, kaj uporablja agent {id} ({name}): njegovo znanje, povezave in vire. Najprej preberi njegovo trenutno opredelitev.',
    makeChatProjectNamed: 'Ta klepet spremeni v projekt z imenom {name}.',
    makeChatProject: 'Ta klepet spremeni v projekt.',
    projectBriefInstruction: 'Uporabi ta povzetek:\n{brief}',
    couldntCreateProject: 'Projekta ni bilo mogoče ustvariti.',
    treeToggle: '{action} strani pod {title}',
    treeHide: 'Skrij',
    treeShow: 'Prikaži',
    goalHint: 'Kaj naj ta agent doseže?',
    instructionsHint: 'Kako naj se agent loti dela in sprejema odločitve.',
    schedulePresetOff: 'Izklopljeno',
    schedulePresetHourly: 'Vsako uro',
    schedulePresetDaily: 'Vsak dan',
    schedulePresetWeekdays: 'Delavniki',
    schedulePresetWeekly: 'Tedensko',
    schedulePresetCustom: 'Po meri',
    pageFallbackAnother: 'drugo stran',
    pageFallbackOverview: 'pregled podjetja',
    noteOptional: 'Opomba (neobvezno)',
    whyThisChange: 'Zakaj ta sprememba?',
    discardChangesConfirm: 'Želite zavreči spremembe? Nič ni bilo shranjeno.',
    editAgentChatPrompt:
      'Pomagaj mi izboljšati agenta {id} ({name}). Najprej preberi njegovo trenutno definicijo.',
    revisionLabel: 'Revizija {number}',
    movePageTitle: 'Premakni “{title}”',
    deletePageTitle: 'Trajno izbriši “{title}”?',
    archivePageTitle: 'Arhiviraj “{title}”?',
    suggestArchivePageTitle: 'Predlagaj arhiviranje “{title}”',
    editedLabel: 'Urejeno',
    editedPageSummary: 'Stran je bila urejena: »{note}«',
    revisionConflict:
      'Stran se je med vašim pregledovanjem spremenila. Znova je naložena: še enkrat preglejte predlog.',
  },
  es: {
    suggestionNewPageUnder: 'Nueva página «{title}» bajo {parent}',
    suggestionMoveUnder: 'Mover esta página bajo {parent}',
    suggestionArchive: 'Archivar esta página',
    suggestionRenameTo: 'Cambiar el nombre de esta página a «{title}»',
    suggestionOverlapNote:
      'Cambia el mismo texto que otra sugerencia, por lo que su cambio se muestra aquí en lugar de en la página.',
    suggestionMismatchNote:
      'No se pudo mostrar su cambio con el contenido actual de la página. Recarga la página para revisarlo.',
    showChange: 'mostrarlo en la página',
    pageMove: 'Mover a…',
    pageArchive: 'Archivar',
    pageRestore: 'Restaurar página',
    pageDeletePermanently: 'Eliminar permanentemente',
    pageExportMarkdown: 'Exportar Markdown',
    pageActions: 'Acciones de la página',
    pageMoveArchiveReason: 'Mueve o archiva primero las páginas que contiene.',
    expectedResult: 'Resultado esperado',
    expectedResultHint: 'Lo que debería entregar una ejecución correcta.',
    resources: 'Conocimiento y conexiones',
    resourcesHint:
      'Dónde buscar. Cada ejecución usa solo los recursos a los que puede acceder la persona que la ejecuta.',
    boundaries: 'Límites y acciones',
    boundariesHint:
      'Qué hacer o evitar. Toda acción conectada sigue necesitando aprobación humana.',
    skillsOptional: 'Habilidades · opcional',
    skillsOptionalNote:
      'Métodos reutilizables que este agente puede usar. Funciona sin ninguna Habilidad.',
    notSpecified: 'No especificado',
    newResultEachRun: 'Un Resultado nuevo en cada ejecución',
    keepResultUpdated: 'Mantener un Resultado actualizado',
    whatEachRunDelivers: 'Qué entrega cada ejecución',
    editAccessRequested: 'Acceso de edición solicitado · esperando una respuesta.',
    askForEditAccess: 'Para cambiarlo, solicita acceso de edición o crea tu propia copia.',
    newAgentGoalHint: 'Lo que debería conseguir.',
    newAgentScheduleHint: 'Bajo demanda o con la programación que elijas.',
    newAgentInstructionsHint: 'Cómo debería hacer el trabajo.',
    newAgentSkillsHint:
      'Encuentra las Habilidades adecuadas para cada ejecución, igual que un chat.',
    newAgentUsesHint: 'El conocimiento de la empresa y las conexiones que puede usar.',
    newAgentDeliversHint:
      'Un Resultado: nuevo en cada ejecución o uno que se mantiene actualizado.',
    agentTab: 'Agente',
    runsTab: 'Ejecuciones',
    versionsTab: 'Versiones',
    testRunTab: 'Ejecución de prueba',
    versionMeta: 'Versión {version}',
    changeProjectBriefPrompt: 'Cambiar el resumen del proyecto:',
    changeProjectPrompt: 'Cambiar este proyecto:',
    changeAgentUsesPrompt:
      'Ayúdame a cambiar lo que usa el agente {id} ({name}): sus conocimientos, conexiones y recursos. Lee primero su definición actual.',
    makeChatProjectNamed: 'Convertir este chat en un proyecto llamado {name}.',
    makeChatProject: 'Convertir este chat en un proyecto.',
    projectBriefInstruction: 'Usa este resumen:\n{brief}',
    couldntCreateProject: 'No se pudo crear el proyecto.',
    treeToggle: '{action} las páginas bajo {title}',
    treeHide: 'Ocultar',
    treeShow: 'Mostrar',
    goalHint: '¿Qué debería lograr este agente?',
    instructionsHint: '¿Cómo debería abordar el trabajo y tomar decisiones?',
    schedulePresetOff: 'Desactivado',
    schedulePresetHourly: 'Cada hora',
    schedulePresetDaily: 'Cada día',
    schedulePresetWeekdays: 'Días laborables',
    schedulePresetWeekly: 'Semanalmente',
    schedulePresetCustom: 'Personalizado',
    pageFallbackAnother: 'otra página',
    pageFallbackOverview: 'la descripción general de la empresa',
    noteOptional: 'Nota (opcional)',
    whyThisChange: '¿Por qué este cambio?',
    discardChangesConfirm: '¿Descartar los cambios? No se ha guardado nada.',
    editAgentChatPrompt:
      'Ayúdame a mejorar el agente {id} ({name}). Lee primero su definición actual.',
    revisionLabel: 'Revisión {number}',
    movePageTitle: 'Mover “{title}”',
    deletePageTitle: '¿Eliminar “{title}” permanentemente?',
    archivePageTitle: '¿Archivar “{title}”?',
    suggestArchivePageTitle: 'Sugerir archivar “{title}”',
    editedLabel: 'Editado',
    editedPageSummary: 'Se editó la página: “{note}”',
    revisionConflict:
      'La página cambió mientras la revisabas. Se volvió a cargar: revisa la sugerencia de nuevo.',
  },
  sv: {
    suggestionNewPageUnder: 'Ny sida ”{title}” under {parent}',
    suggestionMoveUnder: 'Flytta den här sidan under {parent}',
    suggestionArchive: 'Arkivera den här sidan',
    suggestionRenameTo: 'Byt namn på den här sidan till ”{title}”',
    suggestionOverlapNote:
      'Den ändrar samma text som ett annat förslag, så ändringen visas här i stället för på sidan.',
    suggestionMismatchNote:
      'Ändringen kunde inte visas mot sidan i dess nuvarande skick. Ladda om sidan för att granska den.',
    showChange: 'visa den på sidan',
    pageMove: 'Flytta till…',
    pageArchive: 'Arkivera',
    pageRestore: 'Återställ sidan',
    pageDeletePermanently: 'Ta bort permanent',
    pageExportMarkdown: 'Exportera Markdown',
    pageActions: 'Sidåtgärder',
    pageMoveArchiveReason: 'Flytta eller arkivera sidorna som ligger under den först.',
    expectedResult: 'Förväntat resultat',
    expectedResultHint: 'Vad en lyckad körning ska leverera.',
    resources: 'Kunskap och anslutningar',
    resourcesHint:
      'Var du ska leta. Varje körning använder bara resurser som den agerande personen har åtkomst till.',
    boundaries: 'Gränser och åtgärder',
    boundariesHint:
      'Vad som ska göras eller undvikas. Varje ansluten åtgärd kräver fortfarande mänskligt godkännande.',
    skillsOptional: 'Färdigheter · valfritt',
    skillsOptionalNote:
      'Återanvändbara metoder som den här agenten kan använda. Den fungerar utan några färdigheter.',
    notSpecified: 'Inte angivet',
    newResultEachRun: 'Ett nytt resultat vid varje körning',
    keepResultUpdated: 'Håll ett resultat uppdaterat',
    whatEachRunDelivers: 'Vad varje körning levererar',
    editAccessRequested: 'Åtkomst för redigering har begärts · väntar på svar.',
    askForEditAccess:
      'Be om åtkomst för redigering eller skapa en egen kopia om du vill ändra den.',
    newAgentGoalHint: 'Vad den ska uppnå.',
    newAgentScheduleHint: 'På begäran eller enligt ett schema som du väljer.',
    newAgentInstructionsHint: 'Hur den ska utföra arbetet.',
    newAgentSkillsHint:
      'Hittar färdigheter som passar varje körning, på samma sätt som en chatt gör.',
    newAgentUsesHint: 'Företagskunskap och de anslutningar den får använda.',
    newAgentDeliversHint:
      'Ett resultat: ett nytt vid varje körning eller ett som hålls uppdaterat.',
    agentTab: 'Agent',
    runsTab: 'Körningar',
    versionsTab: 'Versioner',
    testRunTab: 'Testkörning',
    versionMeta: 'Version {version}',
    changeProjectBriefPrompt: 'Ändra projektbeskrivningen:',
    changeProjectPrompt: 'Ändra det här projektet:',
    changeAgentUsesPrompt:
      'Hjälp mig ändra vad agenten {id} ({name}) använder: dess kunskap, anslutningar och resurser. Läs först den aktuella definitionen.',
    makeChatProjectNamed: 'Gör den här chatten till ett projekt som heter {name}.',
    makeChatProject: 'Gör den här chatten till ett projekt.',
    projectBriefInstruction: 'Använd detta som beskrivning:\n{brief}',
    couldntCreateProject: 'Det gick inte att skapa projektet.',
    treeToggle: '{action} sidorna under {title}',
    treeHide: 'Dölj',
    treeShow: 'Visa',
    goalHint: 'Vad ska den här agenten uppnå?',
    instructionsHint: 'Hur agenten ska arbeta och fatta beslut.',
    schedulePresetOff: 'Av',
    schedulePresetHourly: 'Varje timme',
    schedulePresetDaily: 'Varje dag',
    schedulePresetWeekdays: 'Vardagar',
    schedulePresetWeekly: 'Varje vecka',
    schedulePresetCustom: 'Anpassat',
    pageFallbackAnother: 'en annan sida',
    pageFallbackOverview: 'företagsöversikten',
    noteOptional: 'Anteckning (valfritt)',
    whyThisChange: 'Varför denna ändring?',
    discardChangesConfirm: 'Vill du kasta dina ändringar? Inget har sparats.',
    editAgentChatPrompt:
      'Hjälp mig att förbättra agenten {id} ({name}). Läs först den aktuella definitionen.',
    revisionLabel: 'Revision {number}',
    movePageTitle: 'Flytta “{title}”',
    deletePageTitle: 'Ta bort “{title}” permanent?',
    archivePageTitle: 'Arkivera “{title}”?',
    suggestArchivePageTitle: 'Föreslå att “{title}” arkiveras',
    editedLabel: 'Redigerad',
    editedPageSummary: 'Redigerade sidan: ”{note}”',
    revisionConflict:
      'Sidan ändrades medan du granskade den. Den har laddats om: granska förslaget igen.',
  },
} satisfies NamespaceCatalog<string>;

export default workspaceEditorsCopy;
